import { QuestionType, ActionTriggerType, AuditStatus, TrainingStatus, RecurrencePattern, DayOfWeek, TriggerCondition } from '../pages/Compliance/Enums/complianceEnums';
import { ActionItem, Audit, Auditor, AuditSchedule, AuditScoreTrend, AuditTemplate, AuditType, ComplianceNotification, DashboardMetrics, JobRole, Region, Store, TrainingAssignment, TrainingCategory, TrainingCertificate, TrainingCompletion, TrainingCompletionTrend, TrainingModule } from '../pages/Compliance/Interfaces/complicanceInterfaces';

// Re-export enums and types for convenience
export { QuestionType, ActionTriggerType, AuditStatus, TrainingStatus, RecurrencePattern, DayOfWeek, TriggerCondition };
export type { ActionItem, Audit, Auditor, AuditSchedule, AuditScoreTrend, AuditTemplate, AuditType, ComplianceNotification, DashboardMetrics, JobRole, Region, Store, TrainingAssignment, TrainingCategory, TrainingCertificate, TrainingCompletion, TrainingCompletionTrend, TrainingModule };

// ======================
// STORES AND REGIONS
// ======================
export const mockStores: Store[] = [
  {
    id: 1,
    storeNumber: 'STR001',
    storeName: 'Downtown Store',
    region: 'North',
    isActive: true,
    franchiseId: 0,
  },
  {
    id: 2,
    storeNumber: 'STR002',
    storeName: 'Westside Store',
    region: 'West',
    isActive: true,
    franchiseId: 0,
  },
  {
    id: 3,
    storeNumber: 'STR003',
    storeName: 'Eastgate Store',
    region: 'East',
    isActive: true,
    franchiseId: 0,
  },
  {
    id: 4,
    storeNumber: 'STR004',
    storeName: 'Southside Store',
    region: 'South',
    isActive: true,
    franchiseId: 0,
  },
];

export const mockRegions: Region[] = [
  {
    id: 'North',
    name: 'North Region',
    storeCount: 1,
  },
  {
    id: 'South',
    name: 'South Region',
    storeCount: 1,
  },
  {
    id: 'East',
    name: 'East Region',
    storeCount: 1,
  },
  {
    id: 'West',
    name: 'West Region',
    storeCount: 1,
  },
];


export const mockAuditors: Auditor[] = [
  { id: 2, name: 'Jane Auditor', email: 'jane@example.com', role: 'Senior Auditor' },
  { id: 3, name: 'Mike Inspector', email: 'mike@example.com', role: 'H&S Inspector' },
  { id: 4, name: 'Sarah Compliance', email: 'sarah@example.com', role: 'Compliance Officer' },
];

// ======================
// AUDIT TYPES
// ======================
export const mockAuditTypes: AuditType[] = [
  {
    id: 1,
    name: 'Food Safety Audit',
    description: 'Comprehensive food safety and hygiene inspection',
    isActive: true,
    createdDate: '2024-01-15T10:00:00Z',
    updatedDate: '2024-06-20T14:30:00Z',
  },
  {
    id: 2,
    name: 'Health & Safety Audit',
    description: 'Workplace health and safety compliance check',
    isActive: true,
    createdDate: '2024-01-15T10:00:00Z',
    updatedDate: null,
  },
  {
    id: 3,
    name: 'Store Cleanliness Audit',
    description: 'Store cleanliness and maintenance inspection',
    isActive: true,
    createdDate: '2024-02-01T09:00:00Z',
    updatedDate: null,
  },
  {
    id: 4,
    name: 'Inventory Management Audit',
    description: 'Stock accuracy and inventory procedures review',
    isActive: true,
    createdDate: '2024-02-10T11:00:00Z',
    updatedDate: null,
  },
  {
    id: 5,
    name: 'Customer Service Audit',
    description: 'Customer service quality and procedures assessment',
    isActive: false,
    createdDate: '2024-03-01T08:00:00Z',
    updatedDate: '2024-09-15T16:00:00Z',
  },
];

// ======================
// AUDIT TEMPLATES
// ======================
export const mockAuditTemplates: AuditTemplate[] = [
  {
    id: 1,
    auditTypeId: 1,
    auditTypeName: 'Food Safety Audit',
    name: 'Food Safety Checklist v3.0',
    description: 'Standard food safety audit template for all stores',
    version: 3,
    isPublished: true,
    createdBy: 1,
    createdByName: 'John Admin',
    createdDate: '2024-01-15T10:00:00Z',
    publishedDate: '2024-01-20T12:00:00Z',
    sections: [
      {
        id: 1,
        auditTemplateId: 1,
        name: 'Food Storage',
        description: 'Temperature control and storage practices',
        displayOrder: 1,
        questions: [
          {
            id: 1,
            sectionId: 1,
            questionText: 'Are all refrigerators maintaining proper temperature (0-4°C)?',
            questionType: QuestionType.Boolean,
            isRequired: true,
            maxScore: 10,
            displayOrder: 1,
            helpText: 'Check temperature logs and thermometers',
            options: [],
            actionTriggers: [
              {
                triggerType: ActionTriggerType.RequireNote,
                triggerCondition: TriggerCondition.Equals,
                triggerValue: false,
                isRequired: true,
                promptText: 'Please explain why refrigerators are not maintaining proper temperature',
                requiredBeforeSubmission: false,
              },
              {
                triggerType: ActionTriggerType.RequirePhoto,
                triggerCondition: TriggerCondition.Equals,
                triggerValue: false,
                isRequired: true,
                promptText: 'Take a photo of the temperature reading',
                requiredBeforeSubmission: false,
              },
              {
                triggerType: ActionTriggerType.CreateActionItem,
                triggerCondition: TriggerCondition.Equals,
                triggerValue: false,
                isRequired: true,
                promptText: 'A corrective action item will be created for temperature control',
                requiredBeforeSubmission: false,
              },
            ],
          },
          {
            id: 2,
            sectionId: 1,
            questionText: 'Record the temperature of the main refrigerator',
            questionType: QuestionType.Number,
            isRequired: true,
            maxScore: 5,
            displayOrder: 2,
            helpText: 'Enter temperature in degrees Celsius',
            options: [],
          },
          {
            id: 3,
            sectionId: 1,
            questionText: 'Storage area cleanliness rating',
            questionType: QuestionType.SingleChoice,
            isRequired: true,
            maxScore: 10,
            displayOrder: 3,
            helpText: 'Rate based on visual inspection',
            options: [
              { id: 1, questionId: 3, optionText: 'Excellent', score: 10, displayOrder: 1 },
              { id: 2, questionId: 3, optionText: 'Good', score: 7, displayOrder: 2 },
              { id: 3, questionId: 3, optionText: 'Fair', score: 4, displayOrder: 3 },
              { id: 4, questionId: 3, optionText: 'Poor', score: 0, displayOrder: 4 },
            ],
            actionTriggers: [
              {
                triggerType: ActionTriggerType.RequireNote,
                triggerCondition: TriggerCondition.Equals,
                triggerValue: 4, // Option ID for "Poor"
                isRequired: true,
                promptText: 'Describe the cleanliness issues found',
                requiredBeforeSubmission: false,
              },
              {
                triggerType: ActionTriggerType.RequirePhoto,
                triggerCondition: TriggerCondition.Equals,
                triggerValue: 4, // Option ID for "Poor"
                isRequired: true,
                promptText: 'Take photos of the cleanliness issues',
                requiredBeforeSubmission: false,
              },
              {
                triggerType: ActionTriggerType.CreateActionItem,
                triggerCondition: TriggerCondition.Equals,
                triggerValue: 4, // Option ID for "Poor"
                isRequired: true,
                promptText: 'A corrective action item will be created for storage cleanliness',
                requiredBeforeSubmission: false,
              },
            ],
          },
          {
            id: 7,
            sectionId: 1,
            questionText: 'Are food items properly labeled with dates?',
            questionType: QuestionType.Boolean,
            isRequired: true,
            maxScore: 10,
            displayOrder: 4,
            helpText: 'Check that all perishables have prep/use-by dates',
            options: [],
            actionTriggers: [
              {
                triggerType: ActionTriggerType.RequireNote,
                triggerCondition: TriggerCondition.Equals,
                triggerValue: false,
                isRequired: true,
                promptText: 'Describe which items are not properly labeled',
                requiredBeforeSubmission: false,
              },
              {
                triggerType: ActionTriggerType.CreateActionItem,
                triggerCondition: TriggerCondition.Equals,
                triggerValue: false,
                isRequired: true,
                promptText: 'A corrective action item will be created for labeling compliance',
                requiredBeforeSubmission: false,
              },
            ],
          },
          {
            id: 8,
            sectionId: 1,
            questionText: 'FIFO (First In, First Out) method being followed?',
            questionType: QuestionType.Boolean,
            isRequired: true,
            maxScore: 10,
            displayOrder: 5,
            helpText: 'Verify older items are in front',
            options: [],
          },
        ],
      },
      {
        id: 2,
        auditTemplateId: 1,
        name: 'Food Handling',
        description: 'Proper food handling and preparation',
        displayOrder: 2,
        questions: [
          {
            id: 4,
            sectionId: 2,
            questionText: 'Are all food handlers wearing proper PPE?',
            questionType: QuestionType.Boolean,
            isRequired: true,
            maxScore: 10,
            displayOrder: 1,
            helpText: 'Check gloves, hairnets, and aprons',
            options: [],
            actionTriggers: [
              {
                triggerType: ActionTriggerType.RequirePhoto,
                triggerCondition: TriggerCondition.Equals,
                triggerValue: false,
                isRequired: true,
                promptText: 'Take a photo showing the PPE violation',
                requiredBeforeSubmission: false,
              },
              {
                triggerType: ActionTriggerType.RequireNote,
                triggerCondition: TriggerCondition.Equals,
                triggerValue: false,
                isRequired: true,
                promptText: 'Specify which PPE items are missing or incorrect',
                requiredBeforeSubmission: false,
              },
            ],
          },
          {
            id: 9,
            sectionId: 2,
            questionText: 'Hand washing procedures being followed?',
            questionType: QuestionType.Boolean,
            isRequired: true,
            maxScore: 10,
            displayOrder: 2,
            helpText: 'Observe staff hand washing between tasks',
            options: [],
          },
          {
            id: 10,
            sectionId: 2,
            questionText: 'Cross-contamination prevention measures',
            questionType: QuestionType.MultipleChoice,
            isRequired: true,
            maxScore: 15,
            displayOrder: 3,
            helpText: 'Select all observed preventive measures',
            options: [
              { id: 5, questionId: 10, optionText: 'Separate cutting boards for raw/cooked', score: 5, displayOrder: 1 },
              { id: 6, questionId: 10, optionText: 'Color-coded utensils in use', score: 5, displayOrder: 2 },
              { id: 7, questionId: 10, optionText: 'Proper food separation in storage', score: 5, displayOrder: 3 },
            ],
          },
          {
            id: 5,
            sectionId: 2,
            questionText: 'Take a photo of the food preparation area',
            questionType: QuestionType.Photo,
            isRequired: false,
            maxScore: null,
            displayOrder: 4,
            helpText: 'Include multiple angles if possible',
            options: [],
          },
        ],
      },
      {
        id: 3,
        auditTemplateId: 1,
        name: 'Cleanliness',
        description: 'Kitchen and food prep area cleanliness',
        displayOrder: 3,
        questions: [
          {
            id: 11,
            sectionId: 3,
            questionText: 'Are all work surfaces clean and sanitized?',
            questionType: QuestionType.Boolean,
            isRequired: true,
            maxScore: 10,
            displayOrder: 1,
            helpText: 'Check counters, cutting boards, and equipment',
            options: [],
          },
          {
            id: 12,
            sectionId: 3,
            questionText: 'Cleaning logs up to date?',
            questionType: QuestionType.Boolean,
            isRequired: true,
            maxScore: 10,
            displayOrder: 2,
            helpText: 'Review daily cleaning checklist completion',
            options: [],
          },
          {
            id: 13,
            sectionId: 3,
            questionText: 'Floor condition assessment',
            questionType: QuestionType.SingleChoice,
            isRequired: true,
            maxScore: 10,
            displayOrder: 3,
            helpText: 'Assess overall floor cleanliness and safety',
            options: [
              { id: 8, questionId: 13, optionText: 'Clean and dry', score: 10, displayOrder: 1 },
              { id: 9, questionId: 13, optionText: 'Clean but wet', score: 7, displayOrder: 2 },
              { id: 10, questionId: 13, optionText: 'Minor debris present', score: 4, displayOrder: 3 },
              { id: 11, questionId: 13, optionText: 'Significant cleaning needed', score: 0, displayOrder: 4 },
            ],
          },
          {
            id: 6,
            sectionId: 3,
            questionText: 'Manager signature confirming review',
            questionType: QuestionType.Signature,
            isRequired: true,
            maxScore: null,
            displayOrder: 4,
            helpText: 'Store manager must sign off',
            options: [],
          },
        ],
      },
    ],
    totalQuestions: 13,
    totalPossibleScore: 100,
    passingScore: null,
    amberScore: null,
  },
  {
    id: 2,
    auditTypeId: 2,
    auditTypeName: 'Health & Safety Audit',
    name: 'H&S Compliance Check v2.1',
    description: 'Health and safety compliance audit',
    version: 2,
    isPublished: true,
    createdBy: 1,
    createdByName: 'John Admin',
    createdDate: '2024-02-01T09:00:00Z',
    publishedDate: '2024-02-05T10:00:00Z',
    sections: [
      {
        id: 4,
        auditTemplateId: 2,
        name: 'Emergency Procedures',
        description: 'Emergency exits and procedures',
        displayOrder: 1,
        questions: [
          {
            id: 7,
            sectionId: 4,
            questionText: 'Are all emergency exits clearly marked and unobstructed?',
            questionType: QuestionType.Boolean,
            isRequired: true,
            maxScore: 10,
            displayOrder: 1,
            helpText: 'Check all exit signs and pathways',
            options: [],
          },
          {
            id: 8,
            sectionId: 4,
            questionText: 'Fire extinguisher inspection status',
            questionType: QuestionType.SingleChoice,
            isRequired: true,
            maxScore: 10,
            displayOrder: 2,
            helpText: 'Check inspection tags',
            options: [
              { id: 5, questionId: 8, optionText: 'All current', score: 10, displayOrder: 1 },
              { id: 6, questionId: 8, optionText: 'Some expired', score: 5, displayOrder: 2 },
              { id: 7, questionId: 8, optionText: 'All expired', score: 0, displayOrder: 3 },
            ],
          },
          {
            id: 14,
            sectionId: 4,
            questionText: 'Number of fire extinguishers on premises',
            questionType: QuestionType.Number,
            isRequired: true,
            maxScore: 5,
            displayOrder: 3,
            helpText: 'Count all visible fire extinguishers',
            options: [],
          },
          {
            id: 15,
            sectionId: 4,
            questionText: 'Emergency evacuation plan posted and visible?',
            questionType: QuestionType.Boolean,
            isRequired: true,
            maxScore: 10,
            displayOrder: 4,
            helpText: 'Check staff areas and break rooms',
            options: [],
          },
        ],
      },
      {
        id: 5,
        auditTemplateId: 2,
        name: 'Equipment Safety',
        description: 'Equipment maintenance and safety',
        displayOrder: 2,
        questions: [
          {
            id: 16,
            sectionId: 5,
            questionText: 'All equipment guards and safety devices in place?',
            questionType: QuestionType.Boolean,
            isRequired: true,
            maxScore: 15,
            displayOrder: 1,
            helpText: 'Check slicers, mixers, and other machinery',
            options: [],
          },
          {
            id: 17,
            sectionId: 5,
            questionText: 'Electrical cords and outlets condition',
            questionType: QuestionType.SingleChoice,
            isRequired: true,
            maxScore: 10,
            displayOrder: 2,
            helpText: 'Look for fraying, exposed wires, overloaded outlets',
            options: [
              { id: 12, questionId: 17, optionText: 'All in good condition', score: 10, displayOrder: 1 },
              { id: 13, questionId: 17, optionText: 'Minor issues noted', score: 5, displayOrder: 2 },
              { id: 14, questionId: 17, optionText: 'Serious safety concerns', score: 0, displayOrder: 3 },
            ],
          },
          {
            id: 18,
            sectionId: 5,
            questionText: 'Take photo of equipment maintenance log',
            questionType: QuestionType.Photo,
            isRequired: false,
            maxScore: null,
            displayOrder: 3,
            helpText: 'Document most recent maintenance entries',
            options: [],
          },
        ],
      },
      {
        id: 10,
        auditTemplateId: 2,
        name: 'Personal Safety',
        description: 'Staff safety equipment and training',
        displayOrder: 3,
        questions: [
          {
            id: 19,
            sectionId: 10,
            questionText: 'First aid kit accessible and fully stocked?',
            questionType: QuestionType.Boolean,
            isRequired: true,
            maxScore: 10,
            displayOrder: 1,
            helpText: 'Check contents against required list',
            options: [],
          },
          {
            id: 20,
            sectionId: 10,
            questionText: 'PPE available for staff',
            questionType: QuestionType.MultipleChoice,
            isRequired: true,
            maxScore: 15,
            displayOrder: 2,
            helpText: 'Select all available PPE types',
            options: [
              { id: 15, questionId: 20, optionText: 'Safety gloves', score: 5, displayOrder: 1 },
              { id: 16, questionId: 20, optionText: 'Cut-resistant gloves', score: 5, displayOrder: 2 },
              { id: 17, questionId: 20, optionText: 'Non-slip footwear', score: 5, displayOrder: 3 },
            ],
          },
          {
            id: 21,
            sectionId: 10,
            questionText: 'Safety training records up to date?',
            questionType: QuestionType.Boolean,
            isRequired: true,
            maxScore: 10,
            displayOrder: 3,
            helpText: 'Verify all staff have completed required safety training',
            options: [],
          },
        ],
      },
    ],
    totalQuestions: 12,
    totalPossibleScore: 95,
    passingScore: null,
    amberScore: null,
  },
  {
    id: 3,
    auditTypeId: 3,
    auditTypeName: 'Store Cleanliness Audit',
    name: 'Store Cleanliness Checklist v1.0',
    description: 'Complete store cleanliness and maintenance inspection',
    version: 1,
    isPublished: true,
    createdBy: 1,
    createdByName: 'John Admin',
    createdDate: '2024-03-10T11:00:00Z',
    publishedDate: '2024-03-15T09:00:00Z',
    sections: [
      {
        id: 6,
        auditTemplateId: 3,
        name: 'Front of House',
        description: 'Customer-facing areas',
        displayOrder: 1,
        questions: [
          {
            id: 9,
            sectionId: 6,
            questionText: 'Are floors clean and free of debris?',
            questionType: QuestionType.Boolean,
            isRequired: true,
            maxScore: 10,
            displayOrder: 1,
            helpText: 'Check all customer areas',
            options: [],
          },
          {
            id: 10,
            sectionId: 6,
            questionText: 'Overall front of house cleanliness',
            questionType: QuestionType.Rating,
            isRequired: true,
            maxScore: 10,
            displayOrder: 2,
            helpText: 'Rate from 1 to 5',
            options: [],
          },
        ],
      },
      {
        id: 7,
        auditTemplateId: 3,
        name: 'Back of House',
        description: 'Staff and storage areas',
        displayOrder: 2,
        questions: [
          {
            id: 11,
            sectionId: 7,
            questionText: 'Storage areas organized and clean?',
            questionType: QuestionType.Boolean,
            isRequired: true,
            maxScore: 10,
            displayOrder: 1,
            helpText: 'Check all storage rooms',
            options: [],
          },
        ],
      },
    ],
    totalQuestions: 3,
    totalPossibleScore: 30,
    passingScore: null,
    amberScore: null,
  },
  {
    id: 4,
    auditTypeId: 4,
    auditTypeName: 'Inventory Management Audit',
    name: 'Inventory Accuracy Check v2.0',
    description: 'Stock counting and inventory procedures verification',
    version: 2,
    isPublished: true,
    createdBy: 1,
    createdByName: 'John Admin',
    createdDate: '2024-04-05T14:00:00Z',
    publishedDate: '2024-04-10T10:00:00Z',
    sections: [
      {
        id: 8,
        auditTemplateId: 4,
        name: 'Stock Accuracy',
        description: 'Physical vs system inventory',
        displayOrder: 1,
        questions: [
          {
            id: 12,
            sectionId: 8,
            questionText: 'Inventory variance percentage',
            questionType: QuestionType.Number,
            isRequired: true,
            maxScore: 20,
            displayOrder: 1,
            helpText: 'Enter variance as percentage',
            options: [],
          },
          {
            id: 13,
            sectionId: 8,
            questionText: 'Are all items properly labeled?',
            questionType: QuestionType.Boolean,
            isRequired: true,
            maxScore: 15,
            displayOrder: 2,
            helpText: 'Check barcodes and labels',
            options: [],
          },
        ],
      },
    ],
    totalQuestions: 2,
    totalPossibleScore: 35,
    passingScore: null,
    amberScore: null,
  },
  {
    id: 5,
    auditTypeId: 1,
    auditTypeName: 'Food Safety Audit',
    name: 'Quick Food Safety Check v1.0',
    description: 'Rapid food safety inspection for daily use',
    version: 1,
    isPublished: false,
    createdBy: 2,
    createdByName: 'Sarah Manager',
    createdDate: '2024-09-15T13:00:00Z',
    publishedDate: null,
    sections: [
      {
        id: 9,
        auditTemplateId: 5,
        name: 'Basic Checks',
        description: 'Essential food safety checks',
        displayOrder: 1,
        questions: [
          {
            id: 14,
            sectionId: 9,
            questionText: 'Temperature logs up to date?',
            questionType: QuestionType.Boolean,
            isRequired: true,
            maxScore: 10,
            displayOrder: 1,
            helpText: 'Check daily temperature records',
            options: [],
          },
        ],
      },
    ],
    totalQuestions: 1,
    totalPossibleScore: 10,
    passingScore: null,
    amberScore: null,
  },
  {
    id: 6,
    auditTypeId: 2,
    auditTypeName: 'Health & Safety Audit',
    name: 'Comprehensive H&S Audit v1.0',
    description: 'Detailed health and safety inspection - draft in progress',
    version: 1,
    isPublished: false,
    createdBy: 1,
    createdByName: 'John Admin',
    createdDate: '2024-09-20T10:00:00Z',
    publishedDate: null,
    sections: [
      {
        id: 10,
        auditTemplateId: 6,
        name: 'Safety Equipment',
        description: 'PPE and safety equipment checks',
        displayOrder: 1,
        questions: [
          {
            id: 15,
            sectionId: 10,
            questionText: 'Are first aid kits fully stocked?',
            questionType: QuestionType.Boolean,
            isRequired: true,
            maxScore: 10,
            displayOrder: 1,
            helpText: 'Check contents against checklist',
            options: [],
          },
          {
            id: 16,
            sectionId: 10,
            questionText: 'Photo of safety equipment storage',
            questionType: QuestionType.Photo,
            isRequired: false,
            maxScore: null,
            displayOrder: 2,
            helpText: 'Document equipment location',
            options: [],
          },
        ],
      },
    ],
    totalQuestions: 2,
    totalPossibleScore: 10,
    passingScore: null,
    amberScore: null,
  },
  {
    id: 7,
    auditTypeId: 3,
    auditTypeName: 'Store Cleanliness Audit',
    name: 'Deep Cleaning Verification v1.0',
    description: 'Monthly deep cleaning verification checklist',
    version: 1,
    isPublished: false,
    createdBy: 2,
    createdByName: 'Sarah Manager',
    createdDate: '2024-09-25T15:30:00Z',
    publishedDate: null,
    sections: [
      {
        id: 11,
        auditTemplateId: 7,
        name: 'Deep Clean Areas',
        description: 'Areas requiring monthly deep clean',
        displayOrder: 1,
        questions: [
          {
            id: 17,
            sectionId: 11,
            questionText: 'High surfaces cleaned and sanitized?',
            questionType: QuestionType.Boolean,
            isRequired: true,
            maxScore: 15,
            displayOrder: 1,
            helpText: 'Check shelves, vents, light fixtures',
            options: [],
          },
          {
            id: 18,
            sectionId: 11,
            questionText: 'Deep clean completion rating',
            questionType: QuestionType.Rating,
            isRequired: true,
            maxScore: 15,
            displayOrder: 2,
            helpText: 'Rate thoroughness 1-5',
            options: [],
          },
        ],
      },
    ],
    totalQuestions: 2,
    totalPossibleScore: 30,
    passingScore: null,
    amberScore: null,
  },
  {
    id: 8,
    auditTypeId: 4,
    auditTypeName: 'Inventory Management Audit',
    name: 'Stock Rotation Check v1.0',
    description: 'FIFO and expiry date verification - needs review',
    version: 1,
    isPublished: false,
    createdBy: 1,
    createdByName: 'John Admin',
    createdDate: '2024-09-28T09:00:00Z',
    publishedDate: null,
    sections: [
      {
        id: 12,
        auditTemplateId: 8,
        name: 'Rotation Practices',
        description: 'First-in-first-out compliance',
        displayOrder: 1,
        questions: [
          {
            id: 19,
            sectionId: 12,
            questionText: 'FIFO system properly implemented?',
            questionType: QuestionType.Boolean,
            isRequired: true,
            maxScore: 20,
            displayOrder: 1,
            helpText: 'Check older stock is at front',
            options: [],
          },
          {
            id: 20,
            sectionId: 12,
            questionText: 'Number of items past expiry date',
            questionType: QuestionType.Number,
            isRequired: true,
            maxScore: 0,
            displayOrder: 2,
            helpText: 'Count expired items found',
            options: [],
          },
        ],
      },
    ],
    totalQuestions: 2,
    totalPossibleScore: 20,
    passingScore: null,
    amberScore: null,
  },
  {
    id: 9,
    auditTypeId: 1,
    auditTypeName: 'Food Safety Audit',
    name: 'Food Safety Checklist v2.5',
    description: 'Previous version - superseded by v3.0',
    version: 2,
    isPublished: true,
    createdBy: 1,
    createdByName: 'John Admin',
    createdDate: '2023-08-10T10:00:00Z',
    publishedDate: '2023-08-15T12:00:00Z',
    sections: [
      {
        id: 13,
        auditTemplateId: 9,
        name: 'Basic Checks',
        description: 'Core food safety requirements',
        displayOrder: 1,
        questions: [
          {
            id: 21,
            sectionId: 13,
            questionText: 'Refrigeration temperature acceptable?',
            questionType: QuestionType.Boolean,
            isRequired: true,
            maxScore: 10,
            displayOrder: 1,
            helpText: 'Check temperature logs',
            options: [],
          },
        ],
      },
    ],
    totalQuestions: 1,
    totalPossibleScore: 10,
    passingScore: null,
    amberScore: null,
  },
];

// ======================
// AUDITS
// ======================
export const mockAudits: Audit[] = [
  {
    id: 1,
    auditTypeId: 1,
    auditTypeName: 'Food Safety Audit',
    templateId: 1,
    templateName: 'Food Safety Checklist v3.0',
    storeNumber: 'STR001',
    storeName: 'Downtown Store',
    scheduledDate: '2025-10-05T09:00:00Z',
    startedDate: '2025-10-05T09:15:00Z',
    completedDate: '2025-10-05T11:30:00Z',
    status: AuditStatus.Submitted,
    assignedToUserId: 2,
    assignedToUserName: 'Jane Auditor', // Legacy field for backward compatibility
    conductedByUserId: 2,
    conductedByUserName: 'Jane Auditor',
    secondSignatureUserId: 1,
    secondSignatureUserName: 'John Admin',
    score: 38,
    maxScore: 40,
    percentageScore: 95.0,
    comments: 'Excellent compliance. All standards met.',
    responses: [
      {
        id: 1,
        auditId: 1,
        questionId: 1,
        responseText: null,
        responseNumber: null,
        responseBoolean: true,
        selectedOptionIds: [],
        photoUrls: [],
        signatureUrl: null,
        score: 10,
      },
      {
        id: 2,
        auditId: 1,
        questionId: 2,
        responseText: null,
        responseNumber: 2.5,
        responseBoolean: null,
        selectedOptionIds: [],
        photoUrls: [],
        signatureUrl: null,
        score: 5,
      },
      {
        id: 3,
        auditId: 1,
        questionId: 3,
        responseText: 'Good',
        responseNumber: null,
        responseBoolean: null,
        selectedOptionIds: [2],
        photoUrls: [],
        signatureUrl: null,
        score: 7,
      },
      {
        id: 4,
        auditId: 1,
        questionId: 4,
        responseText: null,
        responseNumber: null,
        responseBoolean: true,
        selectedOptionIds: [],
        photoUrls: [],
        signatureUrl: null,
        score: 10,
      },
      {
        id: 5,
        auditId: 1,
        questionId: 5,
        responseText: null,
        responseNumber: null,
        responseBoolean: null,
        selectedOptionIds: [],
        photoUrls: ['https://via.placeholder.com/400x300?text=Food+Prep+Area'],
        signatureUrl: null,
        score: null,
      },
      {
        id: 6,
        auditId: 1,
        questionId: 6,
        responseText: null,
        responseNumber: null,
        responseBoolean: null,
        selectedOptionIds: [],
        photoUrls: [],
        signatureUrl: 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mNk+M9QDwADhgGAWjR9awAAAABJRU5ErkJggg==',
        score: null,
      },
    ],
    actionItemsCount: 1,
    firstSignatureData: null,
    secondSignatureData: null,
    firstSignatureUserId: null,
    firstSignatureUserName: null,
    firstSignatureDate: null,
    secondSignatureDate: null,
    submittedOn: null,
    hasPassed: null,
  },
  {
    id: 2,
    auditTypeId: 1,
    auditTypeName: 'Food Safety Audit',
    templateId: 1,
    templateName: 'Food Safety Checklist v3.0',
    storeNumber: 'STR002',
    storeName: 'Westside Store',
    scheduledDate: '2025-10-07T10:00:00Z',
    startedDate: '2025-10-07T10:15:00Z',
    completedDate: '2025-10-07T12:00:00Z',
    status: AuditStatus.Submitted,
    assignedToUserId: 3,
    assignedToUserName: 'Bob Inspector', // Legacy field for backward compatibility
    conductedByUserId: 3,
    conductedByUserName: 'Bob Inspector',
    secondSignatureUserId: 1,
    secondSignatureUserName: 'John Admin',
    score: 36,
    maxScore: 40,
    percentageScore: 90.0,
    comments: 'Good compliance overall. Minor improvements suggested.',
    responses: [
      {
        id: 21,
        auditId: 2,
        questionId: 1,
        responseText: null,
        responseNumber: null,
        responseBoolean: true,
        selectedOptionIds: [],
        photoUrls: [],
        signatureUrl: null,
        score: 10,
      },
      {
        id: 22,
        auditId: 2,
        questionId: 2,
        responseText: null,
        responseNumber: 3.2,
        responseBoolean: null,
        selectedOptionIds: [],
        photoUrls: [],
        signatureUrl: null,
        score: 5,
      },
      {
        id: 23,
        auditId: 2,
        questionId: 3,
        responseText: 'Good',
        responseNumber: null,
        responseBoolean: null,
        selectedOptionIds: [2],
        photoUrls: [],
        signatureUrl: null,
        score: 7,
      },
      {
        id: 24,
        auditId: 2,
        questionId: 7,
        responseText: null,
        responseNumber: null,
        responseBoolean: true,
        selectedOptionIds: [],
        photoUrls: [],
        signatureUrl: null,
        score: 10,
      },
      {
        id: 25,
        auditId: 2,
        questionId: 8,
        responseText: null,
        responseNumber: null,
        responseBoolean: false,
        selectedOptionIds: [],
        photoUrls: [],
        signatureUrl: null,
        score: 0,
        actionNote: 'One staff member in back prep area was not wearing hairnet. Reminded them immediately and they complied.',
      },
      {
        id: 26,
        auditId: 2,
        questionId: 4,
        responseText: null,
        responseNumber: null,
        responseBoolean: true,
        selectedOptionIds: [],
        photoUrls: [],
        signatureUrl: null,
        score: 10,
      },
      {
        id: 27,
        auditId: 2,
        questionId: 9,
        responseText: null,
        responseNumber: null,
        responseBoolean: true,
        selectedOptionIds: [],
        photoUrls: [],
        signatureUrl: null,
        score: 10,
      },
      {
        id: 28,
        auditId: 2,
        questionId: 10,
        responseText: null,
        responseNumber: null,
        responseBoolean: null,
        selectedOptionIds: [5, 6, 7],
        photoUrls: [],
        signatureUrl: null,
        score: 15,
      },
      {
        id: 29,
        auditId: 2,
        questionId: 5,
        responseText: null,
        responseNumber: null,
        responseBoolean: null,
        selectedOptionIds: [],
        photoUrls: ['https://via.placeholder.com/400x300?text=Prep+Area+Clean'],
        signatureUrl: null,
        score: null,
      },
      {
        id: 30,
        auditId: 2,
        questionId: 11,
        responseText: null,
        responseNumber: null,
        responseBoolean: true,
        selectedOptionIds: [],
        photoUrls: [],
        signatureUrl: null,
        score: 10,
      },
      {
        id: 31,
        auditId: 2,
        questionId: 12,
        responseText: null,
        responseNumber: null,
        responseBoolean: true,
        selectedOptionIds: [],
        photoUrls: [],
        signatureUrl: null,
        score: 10,
      },
      {
        id: 32,
        auditId: 2,
        questionId: 13,
        responseText: 'Clean and dry',
        responseNumber: null,
        responseBoolean: null,
        selectedOptionIds: [8],
        photoUrls: [],
        signatureUrl: null,
        score: 10,
      },
      {
        id: 33,
        auditId: 2,
        questionId: 6,
        responseText: null,
        responseNumber: null,
        responseBoolean: null,
        selectedOptionIds: [],
        photoUrls: [],
        signatureUrl: 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mNk+M9QDwADhgGAWjR9awAAAABJRU5ErkJggg==',
        score: null,
      },
    ],
    actionItemsCount: 0,
    firstSignatureData: null,
    secondSignatureData: null,
    firstSignatureUserId: null,
    firstSignatureUserName: null,
    firstSignatureDate: null,
    secondSignatureDate: null,
    submittedOn: null,
    hasPassed: null,
  },
  {
    id: 3,
    auditTypeId: 2,
    auditTypeName: 'Health & Safety Audit',
    templateId: 2,
    templateName: 'H&S Compliance Check v2.1',
    storeNumber: 'STR001',
    storeName: 'Downtown Store',
    scheduledDate: '2025-10-08T14:00:00Z',
    startedDate: '2025-10-08T14:05:00Z',
    completedDate: '2025-10-08T15:20:00Z',
    status: AuditStatus.Submitted,
    assignedToUserId: 2,
    assignedToUserName: 'Jane Asuditor', // Legacy field for backward compatibility
    conductedByUserId: 2,
    conductedByUserName: 'Jane Auditor',
    secondSignatureUserId: null,
    secondSignatureUserName: null,
    score: 19,
    maxScore: 20,
    percentageScore: 95.0,
    comments: 'Excellent safety compliance. All procedures followed.',
    responses: [
      {
        id: 7,
        auditId: 3,
        questionId: 7,
        responseText: null,
        responseNumber: null,
        responseBoolean: true,
        selectedOptionIds: [],
        photoUrls: [],
        signatureUrl: null,
        score: 10,
      },
      {
        id: 8,
        auditId: 3,
        questionId: 8,
        responseText: 'All current',
        responseNumber: null,
        responseBoolean: null,
        selectedOptionIds: [5],
        photoUrls: [],
        signatureUrl: null,
        score: 9,
      },
    ],
    actionItemsCount: 0,
    firstSignatureData: null,
    secondSignatureData: null,
    firstSignatureUserId: null,
    firstSignatureUserName: null,
    firstSignatureDate: null,
    secondSignatureDate: null,
    submittedOn: null,
    hasPassed: null,
  },
  {
    id: 4,
    auditTypeId: 1,
    auditTypeName: 'Food Safety Audit',
    templateId: 1,
    templateName: 'Food Safety Checklist v3.0',
    storeNumber: 'STR003',
    storeName: 'Eastgate Store',
    scheduledDate: '2025-10-09T09:00:00Z',
    startedDate: '2025-10-09T09:10:00Z',
    completedDate: '2025-10-09T10:45:00Z',
    status: AuditStatus.Submitted,
    assignedToUserId: 3,
    assignedToUserName: 'Bob Inspector',
    conductedByUserId: 3,
    conductedByUserName: 'Bob Inspector',
    secondSignatureUserId: 1,
    secondSignatureUserName: 'John Admin',
    score: 37,
    maxScore: 40,
    percentageScore: 92.5,
    comments: 'Very good compliance standards maintained.',
    responses: [
      {
        id: 41,
        auditId: 4,
        questionId: 1,
        responseText: null,
        responseNumber: null,
        responseBoolean: true,
        selectedOptionIds: [],
        photoUrls: [],
        signatureUrl: null,
        score: 10,
      },
      {
        id: 42,
        auditId: 4,
        questionId: 2,
        responseText: null,
        responseNumber: 1.8,
        responseBoolean: null,
        selectedOptionIds: [],
        photoUrls: [],
        signatureUrl: null,
        score: 5,
      },
      {
        id: 43,
        auditId: 4,
        questionId: 3,
        responseText: 'Excellent',
        responseNumber: null,
        responseBoolean: null,
        selectedOptionIds: [1],
        photoUrls: [],
        signatureUrl: null,
        score: 10,
      },
      {
        id: 44,
        auditId: 4,
        questionId: 7,
        responseText: null,
        responseNumber: null,
        responseBoolean: true,
        selectedOptionIds: [],
        photoUrls: [],
        signatureUrl: null,
        score: 10,
      },
      {
        id: 45,
        auditId: 4,
        questionId: 8,
        responseText: null,
        responseNumber: null,
        responseBoolean: true,
        selectedOptionIds: [],
        photoUrls: [],
        signatureUrl: null,
        score: 10,
      },
      {
        id: 46,
        auditId: 4,
        questionId: 4,
        responseText: null,
        responseNumber: null,
        responseBoolean: true,
        selectedOptionIds: [],
        photoUrls: [],
        signatureUrl: null,
        score: 10,
      },
      {
        id: 47,
        auditId: 4,
        questionId: 9,
        responseText: null,
        responseNumber: null,
        responseBoolean: true,
        selectedOptionIds: [],
        photoUrls: [],
        signatureUrl: null,
        score: 10,
      },
      {
        id: 48,
        auditId: 4,
        questionId: 10,
        responseText: null,
        responseNumber: null,
        responseBoolean: null,
        selectedOptionIds: [5, 6],
        photoUrls: [],
        signatureUrl: null,
        score: 10,
      },
      {
        id: 49,
        auditId: 4,
        questionId: 5,
        responseText: null,
        responseNumber: null,
        responseBoolean: null,
        selectedOptionIds: [],
        photoUrls: ['https://via.placeholder.com/400x300?text=Kitchen+Overview', 'https://via.placeholder.com/400x300?text=Storage+Area'],
        signatureUrl: null,
        score: null,
      },
      {
        id: 50,
        auditId: 4,
        questionId: 11,
        responseText: null,
        responseNumber: null,
        responseBoolean: true,
        selectedOptionIds: [],
        photoUrls: [],
        signatureUrl: null,
        score: 10,
      },
      {
        id: 51,
        auditId: 4,
        questionId: 12,
        responseText: null,
        responseNumber: null,
        responseBoolean: true,
        selectedOptionIds: [],
        photoUrls: [],
        signatureUrl: null,
        score: 10,
      },
      {
        id: 52,
        auditId: 4,
        questionId: 13,
        responseText: 'Clean and dry',
        responseNumber: null,
        responseBoolean: null,
        selectedOptionIds: [8],
        photoUrls: [],
        signatureUrl: null,
        score: 10,
      },
      {
        id: 53,
        auditId: 4,
        questionId: 6,
        responseText: null,
        responseNumber: null,
        responseBoolean: null,
        selectedOptionIds: [],
        photoUrls: [],
        signatureUrl: 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mNk+M9QDwADhgGAWjR9awAAAABJRU5ErkJggg==',
        score: null,
      },
    ],
    actionItemsCount: 0,
    firstSignatureData: null,
    secondSignatureData: null,
    firstSignatureUserId: null,
    firstSignatureUserName: null,
    firstSignatureDate: null,
    secondSignatureDate: null,
    submittedOn: null,
    hasPassed: null,
  },
  {
    id: 5,
    auditTypeId: 1,
    auditTypeName: 'Food Safety Audit',
    templateId: 1,
    templateName: 'Food Safety Checklist v3.0',
    storeNumber: 'STR004',
    storeName: 'Southside Store',
    scheduledDate: '2025-10-06T14:00:00Z',
    startedDate: '2025-10-06T14:10:00Z',
    completedDate: '2025-10-06T15:50:00Z',
    status: AuditStatus.Submitted,
    assignedToUserId: 2,
    assignedToUserName: 'Jane Auditor',
    conductedByUserId: 2,
    conductedByUserName: 'Jane Auditor',
    secondSignatureUserId: 1,
    secondSignatureUserName: 'John Admin',
    score: 38,
    maxScore: 40,
    percentageScore: 95.0,
    comments: 'Excellent performance across all areas.',
    responses: [
      {
        id: 61,
        auditId: 5,
        questionId: 1,
        responseText: null,
        responseNumber: null,
        responseBoolean: true,
        selectedOptionIds: [],
        photoUrls: [],
        signatureUrl: null,
        score: 10,
      },
      {
        id: 62,
        auditId: 5,
        questionId: 2,
        responseText: null,
        responseNumber: 2.1,
        responseBoolean: null,
        selectedOptionIds: [],
        photoUrls: [],
        signatureUrl: null,
        score: 5,
      },
      {
        id: 63,
        auditId: 5,
        questionId: 3,
        responseText: 'Excellent',
        responseNumber: null,
        responseBoolean: null,
        selectedOptionIds: [1],
        photoUrls: [],
        signatureUrl: null,
        score: 10,
      },
      {
        id: 64,
        auditId: 5,
        questionId: 7,
        responseText: null,
        responseNumber: null,
        responseBoolean: true,
        selectedOptionIds: [],
        photoUrls: [],
        signatureUrl: null,
        score: 10,
      },
      {
        id: 65,
        auditId: 5,
        questionId: 8,
        responseText: null,
        responseNumber: null,
        responseBoolean: true,
        selectedOptionIds: [],
        photoUrls: [],
        signatureUrl: null,
        score: 10,
      },
      {
        id: 66,
        auditId: 5,
        questionId: 4,
        responseText: null,
        responseNumber: null,
        responseBoolean: true,
        selectedOptionIds: [],
        photoUrls: [],
        signatureUrl: null,
        score: 10,
      },
      {
        id: 67,
        auditId: 5,
        questionId: 9,
        responseText: null,
        responseNumber: null,
        responseBoolean: true,
        selectedOptionIds: [],
        photoUrls: [],
        signatureUrl: null,
        score: 10,
      },
      {
        id: 68,
        auditId: 5,
        questionId: 10,
        responseText: null,
        responseNumber: null,
        responseBoolean: null,
        selectedOptionIds: [5, 7],
        photoUrls: [],
        signatureUrl: null,
        score: 10,
      },
      {
        id: 69,
        auditId: 5,
        questionId: 5,
        responseText: null,
        responseNumber: null,
        responseBoolean: null,
        selectedOptionIds: [],
        photoUrls: ['https://via.placeholder.com/400x300?text=Prep+Station'],
        signatureUrl: null,
        score: null,
      },
      {
        id: 70,
        auditId: 5,
        questionId: 11,
        responseText: null,
        responseNumber: null,
        responseBoolean: true,
        selectedOptionIds: [],
        photoUrls: [],
        signatureUrl: null,
        score: 10,
      },
      {
        id: 71,
        auditId: 5,
        questionId: 12,
        responseText: null,
        responseNumber: null,
        responseBoolean: true,
        selectedOptionIds: [],
        photoUrls: [],
        signatureUrl: null,
        score: 10,
      },
      {
        id: 72,
        auditId: 5,
        questionId: 13,
        responseText: 'Clean but wet',
        responseNumber: null,
        responseBoolean: null,
        selectedOptionIds: [9],
        photoUrls: [],
        signatureUrl: null,
        score: 7,
      },
      {
        id: 73,
        auditId: 5,
        questionId: 6,
        responseText: null,
        responseNumber: null,
        responseBoolean: null,
        selectedOptionIds: [],
        photoUrls: [],
        signatureUrl: 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mNk+M9QDwADhgGAWjR9awAAAABJRU5ErkJggg==',
        score: null,
      },
    ],
    actionItemsCount: 0,
    firstSignatureData: null,
    secondSignatureData: null,
    firstSignatureUserId: null,
    firstSignatureUserName: null,
    firstSignatureDate: null,
    secondSignatureDate: null,
    submittedOn: null,
    hasPassed: null,
  },
  {
    id: 6,
    auditTypeId: 2,
    auditTypeName: 'Health & Safety Audit',
    templateId: 2,
    templateName: 'H&S Compliance Check v2.1',
    storeNumber: 'STR002',
    storeName: 'Westside Store',
    scheduledDate: '2025-10-04T11:00:00Z',
    startedDate: '2025-10-04T11:15:00Z',
    completedDate: '2025-10-04T12:30:00Z',
    status: AuditStatus.Submitted,
    assignedToUserName: 'Bob Inspector',
    conductedByUserId: 3,
    conductedByUserName: 'Bob Inspector',
    secondSignatureUserId: 1,
    secondSignatureUserName: 'John Admin',
    score: 18,
    maxScore: 20,
    percentageScore: 90.0,
    comments: 'Good safety standards maintained.',
    responses: [
      {
        id: 81,
        auditId: 6,
        questionId: 7,
        responseText: null,
        responseNumber: null,
        responseBoolean: true,
        selectedOptionIds: [],
        photoUrls: [],
        signatureUrl: null,
        score: 10,
      },
      {
        id: 82,
        auditId: 6,
        questionId: 8,
        responseText: 'All current',
        responseNumber: null,
        responseBoolean: null,
        selectedOptionIds: [5],
        photoUrls: [],
        signatureUrl: null,
        score: 10,
      },
      {
        id: 83,
        auditId: 6,
        questionId: 14,
        responseText: null,
        responseNumber: 6,
        responseBoolean: null,
        selectedOptionIds: [],
        photoUrls: [],
        signatureUrl: null,
        score: 5,
      },
      {
        id: 84,
        auditId: 6,
        questionId: 15,
        responseText: null,
        responseNumber: null,
        responseBoolean: true,
        selectedOptionIds: [],
        photoUrls: [],
        signatureUrl: null,
        score: 10,
      },
      {
        id: 85,
        auditId: 6,
        questionId: 16,
        responseText: null,
        responseNumber: null,
        responseBoolean: true,
        selectedOptionIds: [],
        photoUrls: [],
        signatureUrl: null,
        score: 15,
      },
      {
        id: 86,
        auditId: 6,
        questionId: 17,
        responseText: 'Minor issues noted',
        responseNumber: null,
        responseBoolean: null,
        selectedOptionIds: [13],
        photoUrls: [],
        signatureUrl: null,
        score: 5,
      },
      {
        id: 87,
        auditId: 6,
        questionId: 18,
        responseText: null,
        responseNumber: null,
        responseBoolean: null,
        selectedOptionIds: [],
        photoUrls: ['https://via.placeholder.com/400x300?text=Maintenance+Log'],
        signatureUrl: null,
        score: null,
      },
      {
        id: 88,
        auditId: 6,
        questionId: 19,
        responseText: null,
        responseNumber: null,
        responseBoolean: true,
        selectedOptionIds: [],
        photoUrls: [],
        signatureUrl: null,
        score: 10,
      },
      {
        id: 89,
        auditId: 6,
        questionId: 20,
        responseText: null,
        responseNumber: null,
        responseBoolean: null,
        selectedOptionIds: [15, 16],
        photoUrls: [],
        signatureUrl: null,
        score: 10,
      },
      {
        id: 90,
        auditId: 6,
        questionId: 21,
        responseText: null,
        responseNumber: null,
        responseBoolean: true,
        selectedOptionIds: [],
        photoUrls: [],
        signatureUrl: null,
        score: 10,
      },
    ],
    actionItemsCount: 0,
    firstSignatureData: null,
    secondSignatureData: null,
    firstSignatureUserId: null,
    firstSignatureUserName: null,
    firstSignatureDate: null,
    secondSignatureDate: null,
    submittedOn: null,
    hasPassed: null,
  },
  {
    id: 7,
    auditTypeId: 1,
    auditTypeName: 'Food Safety Audit',
    templateId: 1,
    templateName: 'Food Safety Checklist v3.0',
    storeNumber: 'STR001',
    storeName: 'Downtown Store',
    scheduledDate: '2025-10-02T08:00:00Z',
    startedDate: '2025-10-02T08:15:00Z',
    completedDate: '2025-10-02T09:45:00Z',
    status: AuditStatus.Submitted,
    assignedToUserName: 'Jane Auditor',
    conductedByUserId: 2,
    conductedByUserName: 'Jane Auditor',
    secondSignatureUserId: 1,
    secondSignatureUserName: 'John Admin',
    score: 39,
    maxScore: 40,
    percentageScore: 97.5,
    comments: 'Outstanding compliance. Best practices observed.',
    responses: [
      {
        id: 101,
        auditId: 7,
        questionId: 1,
        responseText: null,
        responseNumber: null,
        responseBoolean: true,
        selectedOptionIds: [],
        photoUrls: [],
        signatureUrl: null,
        score: 10,
      },
      {
        id: 102,
        auditId: 7,
        questionId: 2,
        responseText: null,
        responseNumber: 1.5,
        responseBoolean: null,
        selectedOptionIds: [],
        photoUrls: [],
        signatureUrl: null,
        score: 5,
      },
      {
        id: 103,
        auditId: 7,
        questionId: 3,
        responseText: 'Excellent',
        responseNumber: null,
        responseBoolean: null,
        selectedOptionIds: [1],
        photoUrls: [],
        signatureUrl: null,
        score: 10,
      },
      {
        id: 104,
        auditId: 7,
        questionId: 7,
        responseText: null,
        responseNumber: null,
        responseBoolean: true,
        selectedOptionIds: [],
        photoUrls: [],
        signatureUrl: null,
        score: 10,
      },
      {
        id: 105,
        auditId: 7,
        questionId: 8,
        responseText: null,
        responseNumber: null,
        responseBoolean: true,
        selectedOptionIds: [],
        photoUrls: [],
        signatureUrl: null,
        score: 10,
      },
      {
        id: 106,
        auditId: 7,
        questionId: 4,
        responseText: null,
        responseNumber: null,
        responseBoolean: true,
        selectedOptionIds: [],
        photoUrls: [],
        signatureUrl: null,
        score: 10,
      },
      {
        id: 107,
        auditId: 7,
        questionId: 9,
        responseText: null,
        responseNumber: null,
        responseBoolean: true,
        selectedOptionIds: [],
        photoUrls: [],
        signatureUrl: null,
        score: 10,
      },
      {
        id: 108,
        auditId: 7,
        questionId: 10,
        responseText: null,
        responseNumber: null,
        responseBoolean: null,
        selectedOptionIds: [5, 6, 7],
        photoUrls: [],
        signatureUrl: null,
        score: 15,
      },
      {
        id: 109,
        auditId: 7,
        questionId: 5,
        responseText: null,
        responseNumber: null,
        responseBoolean: null,
        selectedOptionIds: [],
        photoUrls: ['https://via.placeholder.com/400x300?text=Perfect+Setup', 'https://via.placeholder.com/400x300?text=Equipment+Check'],
        signatureUrl: null,
        score: null,
      },
      {
        id: 110,
        auditId: 7,
        questionId: 11,
        responseText: null,
        responseNumber: null,
        responseBoolean: true,
        selectedOptionIds: [],
        photoUrls: [],
        signatureUrl: null,
        score: 10,
      },
      {
        id: 111,
        auditId: 7,
        questionId: 12,
        responseText: null,
        responseNumber: null,
        responseBoolean: true,
        selectedOptionIds: [],
        photoUrls: [],
        signatureUrl: null,
        score: 10,
      },
      {
        id: 112,
        auditId: 7,
        questionId: 13,
        responseText: 'Clean and dry',
        responseNumber: null,
        responseBoolean: null,
        selectedOptionIds: [8],
        photoUrls: [],
        signatureUrl: null,
        score: 10,
      },
      {
        id: 113,
        auditId: 7,
        questionId: 6,
        responseText: null,
        responseNumber: null,
        responseBoolean: null,
        selectedOptionIds: [],
        photoUrls: [],
        signatureUrl: 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mNk+M9QDwADhgGAWjR9awAAAABJRU5ErkJggg==',
        score: null,
      },
    ],
    actionItemsCount: 0,
    firstSignatureData: null,
    secondSignatureData: null,
    firstSignatureUserId: null,
    firstSignatureUserName: null,
    firstSignatureDate: null,
    secondSignatureDate: null,
    submittedOn: null,
    hasPassed: null,
  },
  {
    id: 8,
    auditTypeId: 2,
    auditTypeName: 'Health & Safety Audit',
    templateId: 2,
    templateName: 'H&S Compliance Check v2.1',
    storeNumber: 'STR003',
    storeName: 'Eastgate Store',
    scheduledDate: '2025-10-03T13:00:00Z',
    startedDate: '2025-10-03T13:10:00Z',
    completedDate: '2025-10-03T14:25:00Z',
    status: AuditStatus.Submitted,
    assignedToUserId: 2,
    assignedToUserName: 'Jane Auditor',
    conductedByUserId: 2,
    conductedByUserName: 'Jane Auditor',
    secondSignatureUserId: 1,
    secondSignatureUserName: 'John Admin',
    score: 19,
    maxScore: 20,
    percentageScore: 95.0,
    comments: 'Excellent safety compliance maintained.',
    responses: [
      {
        id: 121,
        auditId: 8,
        questionId: 7,
        responseText: null,
        responseNumber: null,
        responseBoolean: true,
        selectedOptionIds: [],
        photoUrls: [],
        signatureUrl: null,
        score: 10,
      },
      {
        id: 122,
        auditId: 8,
        questionId: 8,
        responseText: 'All current',
        responseNumber: null,
        responseBoolean: null,
        selectedOptionIds: [5],
        photoUrls: [],
        signatureUrl: null,
        score: 10,
      },
      {
        id: 123,
        auditId: 8,
        questionId: 14,
        responseText: null,
        responseNumber: 8,
        responseBoolean: null,
        selectedOptionIds: [],
        photoUrls: [],
        signatureUrl: null,
        score: 5,
      },
      {
        id: 124,
        auditId: 8,
        questionId: 15,
        responseText: null,
        responseNumber: null,
        responseBoolean: true,
        selectedOptionIds: [],
        photoUrls: [],
        signatureUrl: null,
        score: 10,
      },
      {
        id: 125,
        auditId: 8,
        questionId: 16,
        responseText: null,
        responseNumber: null,
        responseBoolean: true,
        selectedOptionIds: [],
        photoUrls: [],
        signatureUrl: null,
        score: 15,
      },
      {
        id: 126,
        auditId: 8,
        questionId: 17,
        responseText: 'All in good condition',
        responseNumber: null,
        responseBoolean: null,
        selectedOptionIds: [12],
        photoUrls: [],
        signatureUrl: null,
        score: 10,
      },
      {
        id: 127,
        auditId: 8,
        questionId: 18,
        responseText: null,
        responseNumber: null,
        responseBoolean: null,
        selectedOptionIds: [],
        photoUrls: ['https://via.placeholder.com/400x300?text=Equipment+Log'],
        signatureUrl: null,
        score: null,
      },
      {
        id: 128,
        auditId: 8,
        questionId: 19,
        responseText: null,
        responseNumber: null,
        responseBoolean: true,
        selectedOptionIds: [],
        photoUrls: [],
        signatureUrl: null,
        score: 10,
      },
      {
        id: 129,
        auditId: 8,
        questionId: 20,
        responseText: null,
        responseNumber: null,
        responseBoolean: null,
        selectedOptionIds: [15, 16, 17],
        photoUrls: [],
        signatureUrl: null,
        score: 15,
      },
      {
        id: 130,
        auditId: 8,
        questionId: 21,
        responseText: null,
        responseNumber: null,
        responseBoolean: true,
        selectedOptionIds: [],
        photoUrls: [],
        signatureUrl: null,
        score: 10,
      },
    ],
    actionItemsCount: 0,
    firstSignatureData: null,
    secondSignatureData: null,
    firstSignatureUserId: null,
    firstSignatureUserName: null,
    firstSignatureDate: null,
    secondSignatureDate: null,
    submittedOn: null,
    hasPassed: null,
  },
  {
    id: 9,
    auditTypeId: 1,
    auditTypeName: 'Food Safety Audit',
    templateId: 1,
    templateName: 'Food Safety Checklist v3.0',
    storeNumber: 'STR002',
    storeName: 'Westside Store',
    scheduledDate: '2025-10-01T09:00:00Z',
    startedDate: '2025-10-01T09:15:00Z',
    completedDate: '2025-10-01T10:45:00Z',
    status: AuditStatus.Submitted,
    assignedToUserId: 3,
    assignedToUserName: 'Bob Inspector',
    conductedByUserId: 3,
    conductedByUserName: 'Bob Inspector',
    secondSignatureUserId: 1,
    secondSignatureUserName: 'John Admin',
    score: 35,
    maxScore: 40,
    percentageScore: 87.5,
    comments: 'Good compliance. Some minor improvements noted.',
    responses: [
      {
        id: 141,
        auditId: 9,
        questionId: 1,
        responseText: null,
        responseNumber: null,
        responseBoolean: true,
        selectedOptionIds: [],
        photoUrls: [],
        signatureUrl: null,
        score: 10,
      },
      {
        id: 142,
        auditId: 9,
        questionId: 2,
        responseText: null,
        responseNumber: 3.8,
        responseBoolean: null,
        selectedOptionIds: [],
        photoUrls: [],
        signatureUrl: null,
        score: 5,
      },
      {
        id: 143,
        auditId: 9,
        questionId: 3,
        responseText: 'Fair',
        responseNumber: null,
        responseBoolean: null,
        selectedOptionIds: [3],
        photoUrls: [],
        signatureUrl: null,
        score: 4,
      },
      {
        id: 144,
        auditId: 9,
        questionId: 7,
        responseText: null,
        responseNumber: null,
        responseBoolean: false,
        selectedOptionIds: [],
        photoUrls: [],
        signatureUrl: null,
        score: 0,
        actionNote: 'Found 3 containers of prepared salad without date labels. Staff immediately labeled them with today\'s date and use-by dates.',
        actionItemCreated: true,
      },
      {
        id: 145,
        auditId: 9,
        questionId: 8,
        responseText: null,
        responseNumber: null,
        responseBoolean: true,
        selectedOptionIds: [],
        photoUrls: [],
        signatureUrl: null,
        score: 10,
      },
      {
        id: 146,
        auditId: 9,
        questionId: 4,
        responseText: null,
        responseNumber: null,
        responseBoolean: true,
        selectedOptionIds: [],
        photoUrls: [],
        signatureUrl: null,
        score: 10,
      },
      {
        id: 147,
        auditId: 9,
        questionId: 9,
        responseText: null,
        responseNumber: null,
        responseBoolean: true,
        selectedOptionIds: [],
        photoUrls: [],
        signatureUrl: null,
        score: 10,
      },
      {
        id: 148,
        auditId: 9,
        questionId: 10,
        responseText: null,
        responseNumber: null,
        responseBoolean: null,
        selectedOptionIds: [5, 6, 7],
        photoUrls: [],
        signatureUrl: null,
        score: 15,
      },
      {
        id: 149,
        auditId: 9,
        questionId: 5,
        responseText: null,
        responseNumber: null,
        responseBoolean: null,
        selectedOptionIds: [],
        photoUrls: ['https://via.placeholder.com/400x300?text=Work+Area'],
        signatureUrl: null,
        score: null,
      },
      {
        id: 150,
        auditId: 9,
        questionId: 11,
        responseText: null,
        responseNumber: null,
        responseBoolean: true,
        selectedOptionIds: [],
        photoUrls: [],
        signatureUrl: null,
        score: 10,
      },
      {
        id: 151,
        auditId: 9,
        questionId: 12,
        responseText: null,
        responseNumber: null,
        responseBoolean: true,
        selectedOptionIds: [],
        photoUrls: [],
        signatureUrl: null,
        score: 10,
      },
      {
        id: 152,
        auditId: 9,
        questionId: 13,
        responseText: 'Clean and dry',
        responseNumber: null,
        responseBoolean: null,
        selectedOptionIds: [8],
        photoUrls: [],
        signatureUrl: null,
        score: 10,
      },
      {
        id: 153,
        auditId: 9,
        questionId: 6,
        responseText: null,
        responseNumber: null,
        responseBoolean: null,
        selectedOptionIds: [],
        photoUrls: [],
        signatureUrl: 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mNk+M9QDwADhgGAWjR9awAAAABJRU5ErkJggg==',
        score: null,
      },
    ],
    actionItemsCount: 1,
    firstSignatureData: null,
    secondSignatureData: null,
    firstSignatureUserId: null,
    firstSignatureUserName: null,
    firstSignatureDate: null,
    secondSignatureDate: null,
    submittedOn: null,
    hasPassed: null,
  },
  {
    id: 10,
    auditTypeId: 2,
    auditTypeName: 'Health & Safety Audit',
    templateId: 2,
    templateName: 'H&S Compliance Check v2.1',
    storeNumber: 'STR004',
    storeName: 'Southside Store',
    scheduledDate: '2025-10-10T10:00:00Z',
    startedDate: '2025-10-10T10:10:00Z',
    completedDate: '2025-10-10T11:30:00Z',
    status: AuditStatus.Submitted,
    assignedToUserId: 2,
    assignedToUserName: 'Jane Auditor',
    conductedByUserId: 2,
    conductedByUserName: 'Jane Auditor',
    secondSignatureUserId: 1,
    secondSignatureUserName: 'John Admin',
    score: 20,
    maxScore: 20,
    percentageScore: 100.0,
    comments: 'Perfect score! Exemplary safety practices.',
    responses: [
      {
        id: 161,
        auditId: 10,
        questionId: 7,
        responseText: null,
        responseNumber: null,
        responseBoolean: true,
        selectedOptionIds: [],
        photoUrls: [],
        signatureUrl: null,
        score: 10,
      },
      {
        id: 162,
        auditId: 10,
        questionId: 8,
        responseText: 'All current',
        responseNumber: null,
        responseBoolean: null,
        selectedOptionIds: [5],
        photoUrls: [],
        signatureUrl: null,
        score: 10,
      },
      {
        id: 163,
        auditId: 10,
        questionId: 14,
        responseText: null,
        responseNumber: 7,
        responseBoolean: null,
        selectedOptionIds: [],
        photoUrls: [],
        signatureUrl: null,
        score: 5,
      },
      {
        id: 164,
        auditId: 10,
        questionId: 15,
        responseText: null,
        responseNumber: null,
        responseBoolean: true,
        selectedOptionIds: [],
        photoUrls: [],
        signatureUrl: null,
        score: 10,
      },
      {
        id: 165,
        auditId: 10,
        questionId: 16,
        responseText: null,
        responseNumber: null,
        responseBoolean: true,
        selectedOptionIds: [],
        photoUrls: [],
        signatureUrl: null,
        score: 15,
      },
      {
        id: 166,
        auditId: 10,
        questionId: 17,
        responseText: 'All in good condition',
        responseNumber: null,
        responseBoolean: null,
        selectedOptionIds: [12],
        photoUrls: [],
        signatureUrl: null,
        score: 10,
      },
      {
        id: 167,
        auditId: 10,
        questionId: 18,
        responseText: null,
        responseNumber: null,
        responseBoolean: null,
        selectedOptionIds: [],
        photoUrls: ['https://via.placeholder.com/400x300?text=Perfect+Maintenance+Record'],
        signatureUrl: null,
        score: null,
      },
      {
        id: 168,
        auditId: 10,
        questionId: 19,
        responseText: null,
        responseNumber: null,
        responseBoolean: true,
        selectedOptionIds: [],
        photoUrls: [],
        signatureUrl: null,
        score: 10,
      },
      {
        id: 169,
        auditId: 10,
        questionId: 20,
        responseText: null,
        responseNumber: null,
        responseBoolean: null,
        selectedOptionIds: [15, 16, 17],
        photoUrls: [],
        signatureUrl: null,
        score: 15,
      },
      {
        id: 170,
        auditId: 10,
        questionId: 21,
        responseText: null,
        responseNumber: null,
        responseBoolean: true,
        selectedOptionIds: [],
        photoUrls: [],
        signatureUrl: null,
        score: 10,
      },
    ],
    actionItemsCount: 0,
    firstSignatureData: null,
    secondSignatureData: null,
    firstSignatureUserId: null,
    firstSignatureUserName: null,
    firstSignatureDate: null,
    secondSignatureDate: null,
    submittedOn: null,
    hasPassed: null,
  },
  {
    id: 11,
    auditTypeId: 1,
    auditTypeName: 'Food Safety Audit',
    templateId: 1,
    templateName: 'Food Safety Checklist v3.0',
    storeNumber: 'STR003',
    storeName: 'Eastgate Store',
    scheduledDate: '2025-10-11T15:00:00Z',
    startedDate: '2025-10-11T15:15:00Z',
    completedDate: '2025-10-11T16:45:00Z',
    status: AuditStatus.Submitted,
    assignedToUserId: 3,
    assignedToUserName: 'Bob Inspector',
    conductedByUserId: 3,
    conductedByUserName: 'Bob Inspector',
    secondSignatureUserId: 1,
    secondSignatureUserName: 'John Admin',
    score: 36,
    maxScore: 40,
    percentageScore: 90.0,
    comments: 'Solid performance. Compliance maintained.',
    responses: [
      {
        id: 181,
        auditId: 11,
        questionId: 1,
        responseText: null,
        responseNumber: null,
        responseBoolean: true,
        selectedOptionIds: [],
        photoUrls: [],
        signatureUrl: null,
        score: 10,
      },
      {
        id: 182,
        auditId: 11,
        questionId: 2,
        responseText: null,
        responseNumber: 2.8,
        responseBoolean: null,
        selectedOptionIds: [],
        photoUrls: [],
        signatureUrl: null,
        score: 5,
      },
      {
        id: 183,
        auditId: 11,
        questionId: 3,
        responseText: 'Good',
        responseNumber: null,
        responseBoolean: null,
        selectedOptionIds: [2],
        photoUrls: [],
        signatureUrl: null,
        score: 7,
      },
      {
        id: 184,
        auditId: 11,
        questionId: 7,
        responseText: null,
        responseNumber: null,
        responseBoolean: true,
        selectedOptionIds: [],
        photoUrls: [],
        signatureUrl: null,
        score: 10,
      },
      {
        id: 185,
        auditId: 11,
        questionId: 8,
        responseText: null,
        responseNumber: null,
        responseBoolean: true,
        selectedOptionIds: [],
        photoUrls: [],
        signatureUrl: null,
        score: 10,
      },
      {
        id: 186,
        auditId: 11,
        questionId: 4,
        responseText: null,
        responseNumber: null,
        responseBoolean: true,
        selectedOptionIds: [],
        photoUrls: [],
        signatureUrl: null,
        score: 10,
      },
      {
        id: 187,
        auditId: 11,
        questionId: 9,
        responseText: null,
        responseNumber: null,
        responseBoolean: true,
        selectedOptionIds: [],
        photoUrls: [],
        signatureUrl: null,
        score: 10,
      },
      {
        id: 188,
        auditId: 11,
        questionId: 10,
        responseText: null,
        responseNumber: null,
        responseBoolean: null,
        selectedOptionIds: [5, 7],
        photoUrls: [],
        signatureUrl: null,
        score: 10,
      },
      {
        id: 189,
        auditId: 11,
        questionId: 5,
        responseText: null,
        responseNumber: null,
        responseBoolean: null,
        selectedOptionIds: [],
        photoUrls: ['https://via.placeholder.com/400x300?text=Standard+Compliance'],
        signatureUrl: null,
        score: null,
      },
      {
        id: 190,
        auditId: 11,
        questionId: 11,
        responseText: null,
        responseNumber: null,
        responseBoolean: true,
        selectedOptionIds: [],
        photoUrls: [],
        signatureUrl: null,
        score: 10,
      },
      {
        id: 191,
        auditId: 11,
        questionId: 12,
        responseText: null,
        responseNumber: null,
        responseBoolean: true,
        selectedOptionIds: [],
        photoUrls: [],
        signatureUrl: null,
        score: 10,
      },
      {
        id: 192,
        auditId: 11,
        questionId: 13,
        responseText: 'Clean but wet',
        responseNumber: null,
        responseBoolean: null,
        selectedOptionIds: [9],
        photoUrls: [],
        signatureUrl: null,
        score: 7,
      },
      {
        id: 193,
        auditId: 11,
        questionId: 6,
        responseText: null,
        responseNumber: null,
        responseBoolean: null,
        selectedOptionIds: [],
        photoUrls: [],
        signatureUrl: 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mNk+M9QDwADhgGAWjR9awAAAABJRU5ErkJggg==',
        score: null,
      },
    ],
    actionItemsCount: 0,
    firstSignatureData: null,
    secondSignatureData: null,
    firstSignatureUserId: null,
    firstSignatureUserName: null,
    firstSignatureDate: null,
    secondSignatureDate: null,
    submittedOn: null,
    hasPassed: null,
  },
  {
    id: 12,
    auditTypeId: 1,
    auditTypeName: 'Food Safety Audit',
    templateId: 1,
    templateName: 'Food Safety Checklist v3.0',
    storeNumber: 'STR004',
    storeName: 'Southside Store',
    scheduledDate: '2025-10-15T09:00:00Z',
    startedDate: null,
    completedDate: null,
    status: AuditStatus.Draft,
    assignedToUserId: 2,
    assignedToUserName: 'Jane Auditor',
    conductedByUserId: null,
    conductedByUserName: null,
    secondSignatureUserId: null,
    secondSignatureUserName: null,
    score: null,
    maxScore: 40,
    percentageScore: null,
    comments: null,
    responses: [],
    actionItemsCount: 0,
    firstSignatureData: null,
    secondSignatureData: null,
    firstSignatureUserId: null,
    firstSignatureUserName: null,
    firstSignatureDate: null,
    secondSignatureDate: null,
    submittedOn: null,
    hasPassed: null,
  },
  {
    id: 13,
    auditTypeId: 2,
    auditTypeName: 'Health & Safety Audit',
    templateId: 2,
    templateName: 'H&S Inspection Checklist v2.1',
    storeNumber: 'STR001',
    storeName: 'Downtown Store',
    scheduledDate: '2025-10-14T10:00:00Z',
    startedDate: '2025-10-12T10:30:00Z',
    completedDate: null,
    status: AuditStatus.Draft,
    assignedToUserId: 3,
    assignedToUserName: 'Mike Inspector',
    conductedByUserId: 3,
    conductedByUserName: 'Mike Inspector',
    secondSignatureUserId: null,
    secondSignatureUserName: null,
    score: null,
    maxScore: 50,
    percentageScore: null,
    comments: 'In progress - partially completed',
    responses: [],
    actionItemsCount: 0,
    firstSignatureData: null,
    secondSignatureData: null,
    firstSignatureUserId: null,
    firstSignatureUserName: null,
    firstSignatureDate: null,
    secondSignatureDate: null,
    submittedOn: null,
    hasPassed: null,
  },
  {
    id: 14,
    auditTypeId: 1,
    auditTypeName: 'Food Safety Audit',
    templateId: 1,
    templateName: 'Food Safety Checklist v3.0',
    storeNumber: 'STR003',
    storeName: 'Eastside Store',
    scheduledDate: '2025-10-18T14:00:00Z',
    startedDate: null,
    completedDate: null,
    status: AuditStatus.Draft,
    assignedToUserId: 2,
    assignedToUserName: 'Jane Auditor',
    conductedByUserId: null,
    conductedByUserName: null,
    secondSignatureUserId: null,
    secondSignatureUserName: null,
    score: null,
    maxScore: 40,
    percentageScore: null,
    comments: null,
    responses: [],
    actionItemsCount: 0,
    firstSignatureData: null,
    secondSignatureData: null,
    firstSignatureUserId: null,
    firstSignatureUserName: null,
    firstSignatureDate: null,
    secondSignatureDate: null,
    submittedOn: null,
    hasPassed: null,
  },
];

// ======================
// TRAINING CATEGORIES
// ======================
export const mockTrainingCategories: TrainingCategory[] = [
  {
    id: 1,
    name: 'Food Safety',
    description: 'Food safety and hygiene training',
    displayOrder: 1,
    isActive: true,
  },
  {
    id: 2,
    name: 'Health & Safety',
    description: 'Workplace health and safety',
    displayOrder: 2,
    isActive: true,
  },
  {
    id: 3,
    name: 'Customer Service',
    description: 'Customer service excellence',
    displayOrder: 3,
    isActive: true,
  },
  {
    id: 4,
    name: 'Compliance',
    description: 'Regulatory compliance training',
    displayOrder: 4,
    isActive: true,
  },
];

// ======================
// TRAINING MODULES
// ======================
export const mockTrainingModules: TrainingModule[] = [
  {
    id: 1,
    categoryId: 1,
    categoryName: 'Food Safety',
    name: 'Food Hygiene Basics',
    description: 'Essential food hygiene and safety practices for all food handlers',
    version: 1,
    status: TrainingStatus.Published,
    estimatedDurationMinutes: 45,
    validityPeriodDays: 365,
    createdBy: 1,
    createdByName: 'John Admin',
    createdDate: '2024-01-10T10:00:00Z',
    publishedDate: '2024-01-15T12:00:00Z',
    contents: [
      {
        id: 1,
        moduleId: 1,
        title: 'Introduction to Food Safety',
        contentType: 'text',
        contentText: 'Food safety is critical to prevent foodborne illnesses...',
        contentUrl: null,
        displayOrder: 1,
        estimatedMinutes: 10,
      },
      {
        id: 2,
        moduleId: 1,
        title: 'Temperature Control',
        contentType: 'video',
        contentText: null,
        contentUrl: 'https://example.com/videos/temp-control.mp4',
        displayOrder: 2,
        estimatedMinutes: 15,
      },
      {
        id: 3,
        moduleId: 1,
        title: 'Personal Hygiene',
        contentType: 'text',
        contentText: 'Proper handwashing and personal hygiene practices...',
        contentUrl: null,
        displayOrder: 3,
        estimatedMinutes: 10,
      },
    ],
    quiz: {
      id: 1,
      moduleId: 1,
      title: 'Food Hygiene Quiz',
      description: 'Test your knowledge of food hygiene basics',
      passingScore: 80,
      questions: [
        {
          id: 1,
          quizId: 1,
          questionText: 'What is the safe temperature range for refrigerators?',
          questionType: QuestionType.SingleChoice,
          points: 10,
          displayOrder: 1,
          answers: [
            { id: 1, questionId: 1, answerText: '0-4°C', isCorrect: true, displayOrder: 1 },
            { id: 2, questionId: 1, answerText: '5-10°C', isCorrect: false, displayOrder: 2 },
            { id: 3, questionId: 1, answerText: '10-15°C', isCorrect: false, displayOrder: 3 },
            { id: 4, questionId: 1, answerText: '-5-0°C', isCorrect: false, displayOrder: 4 },
          ],
        },
        {
          id: 2,
          quizId: 1,
          questionText: 'How long should you wash your hands?',
          questionType: QuestionType.SingleChoice,
          points: 10,
          displayOrder: 2,
          answers: [
            { id: 5, questionId: 2, answerText: '5 seconds', isCorrect: false, displayOrder: 1 },
            { id: 6, questionId: 2, answerText: '20 seconds', isCorrect: true, displayOrder: 2 },
            { id: 7, questionId: 2, answerText: '1 minute', isCorrect: false, displayOrder: 3 },
            { id: 8, questionId: 2, answerText: '2 minutes', isCorrect: false, displayOrder: 4 },
          ],
        },
      ],
    },
    totalQuestions: 2,
    requiredForJobRoles: ['Kitchen Staff', 'Store Manager'],
    content: [
      {
        id: 1,
        moduleId: 1,
        title: 'Introduction to Food Safety',
        contentType: 'text' as const,
        contentText: '<p>Food safety is critical to prevent foodborne illnesses. In this module, you will learn about the key principles of food hygiene, proper handling techniques, and how to maintain a safe food environment.</p><p><strong>Key topics covered:</strong></p><ul><li>Understanding foodborne pathogens</li><li>Temperature danger zones</li><li>Cross-contamination prevention</li><li>Personal hygiene standards</li><li>Cleaning and sanitization procedures</li></ul>',
        contentUrl: null,
        displayOrder: 1,
        estimatedMinutes: 10,
      },
      {
        id: 2,
        moduleId: 1,
        title: 'Temperature Control',
        contentType: 'text' as const,
        contentText: '<p>Proper temperature control is one of the most important aspects of food safety. Bacteria multiply rapidly in the "danger zone" between 5°C and 60°C.</p><p><strong>Cold Storage Guidelines:</strong></p><ul><li>Refrigerators: 0-4°C</li><li>Freezers: -18°C or below</li><li>Check temperatures at least twice daily</li><li>Keep thermometers visible</li></ul><p><strong>Hot Holding:</strong></p><ul><li>Keep hot foods above 60°C</li><li>Reheat food to at least 75°C</li><li>Use a food thermometer to verify temperatures</li></ul>',
        contentUrl: null,
        displayOrder: 2,
        estimatedMinutes: 15,
      },
      {
        id: 3,
        moduleId: 1,
        title: 'Personal Hygiene',
        contentType: 'text' as const,
        contentText: '<p>Personal hygiene is crucial for food handlers to prevent contamination.</p><p><strong>Handwashing Requirements:</strong></p><ul><li>Wash hands for at least 20 seconds</li><li>Use warm water and soap</li><li>Wash before starting work, after breaks, after handling raw food</li><li>Dry with paper towels or hand dryer</li></ul><p><strong>Personal Protective Equipment (PPE):</strong></p><ul><li>Clean aprons or uniforms</li><li>Hair nets or caps</li><li>Gloves when appropriate</li><li>No jewelry except plain wedding bands</li></ul><p><strong>Illness Policy:</strong></p><ul><li>Report any illness to management</li><li>Do not work with food if you have vomiting or diarrhea</li><li>Cover cuts and wounds with waterproof dressings</li></ul>',
        contentUrl: null,
        displayOrder: 3,
        estimatedMinutes: 10,
      },
    ],
    quizzes: [
      {
        id: 1,
        moduleId: 1,
        title: 'Food Hygiene Quiz',
        description: 'Test your knowledge of food hygiene basics',
        passingScore: 80,
        questions: [
          {
            id: 1,
            quizId: 1,
            questionText: 'What is the safe temperature range for refrigerators?',
            questionType: QuestionType.SingleChoice,
            points: 10,
            displayOrder: 1,
            answers: [
              { id: 1, questionId: 1, answerText: '0-4°C', isCorrect: true, displayOrder: 1 },
              { id: 2, questionId: 1, answerText: '5-10°C', isCorrect: false, displayOrder: 2 },
              { id: 3, questionId: 1, answerText: '10-15°C', isCorrect: false, displayOrder: 3 },
              { id: 4, questionId: 1, answerText: '-5-0°C', isCorrect: false, displayOrder: 4 },
            ],
          },
          {
            id: 2,
            quizId: 1,
            questionText: 'How long should you wash your hands?',
            questionType: QuestionType.SingleChoice,
            points: 10,
            displayOrder: 2,
            answers: [
              { id: 5, questionId: 2, answerText: '5 seconds', isCorrect: false, displayOrder: 1 },
              { id: 6, questionId: 2, answerText: '20 seconds', isCorrect: true, displayOrder: 2 },
              { id: 7, questionId: 2, answerText: '1 minute', isCorrect: false, displayOrder: 3 },
              { id: 8, questionId: 2, answerText: '2 minutes', isCorrect: false, displayOrder: 4 },
            ],
          },
          {
            id: 3,
            quizId: 1,
            questionText: 'What is the temperature danger zone for food?',
            questionType: QuestionType.SingleChoice,
            points: 10,
            displayOrder: 3,
            answers: [
              { id: 9, questionId: 3, answerText: '5-60°C', isCorrect: true, displayOrder: 1 },
              { id: 10, questionId: 3, answerText: '0-100°C', isCorrect: false, displayOrder: 2 },
              { id: 11, questionId: 3, answerText: '10-50°C', isCorrect: false, displayOrder: 3 },
              { id: 12, questionId: 3, answerText: '-5-25°C', isCorrect: false, displayOrder: 4 },
            ],
          },
        ],
      },
    ],
    durationMinutes: 45,
    passingScore: 80,
    validityDays: 365,
    isActive: true,
  },
  {
    id: 2,
    categoryId: 2,
    categoryName: 'Health & Safety',
    name: 'Workplace Safety Fundamentals',
    description: 'Essential workplace health and safety practices',
    version: 1,
    status: TrainingStatus.Published,
    estimatedDurationMinutes: 30,
    validityPeriodDays: 730,
    createdBy: 1,
    createdByName: 'John Admin',
    createdDate: '2024-01-20T10:00:00Z',
    publishedDate: '2024-01-25T12:00:00Z',
    contents: [
      {
        id: 4,
        moduleId: 2,
        title: 'Emergency Procedures',
        contentType: 'text',
        contentText: 'Know your emergency exits and assembly points...',
        contentUrl: null,
        displayOrder: 1,
        estimatedMinutes: 10,
      },
      {
        id: 5,
        moduleId: 2,
        title: 'Fire Safety',
        contentType: 'video',
        contentText: null,
        contentUrl: 'https://example.com/videos/fire-safety.mp4',
        displayOrder: 2,
        estimatedMinutes: 15,
      },
    ],
    quiz: {
      id: 2,
      moduleId: 2,
      title: 'Safety Quiz',
      description: 'Test your safety knowledge',
      passingScore: 70,
      questions: [
        {
          id: 3,
          quizId: 2,
          questionText: 'What does PPE stand for?',
          questionType: QuestionType.SingleChoice,
          points: 10,
          displayOrder: 1,
          answers: [
            { id: 9, questionId: 3, answerText: 'Personal Protective Equipment', isCorrect: true, displayOrder: 1 },
            { id: 10, questionId: 3, answerText: 'Public Protection Equipment', isCorrect: false, displayOrder: 2 },
            { id: 11, questionId: 3, answerText: 'Professional Protective Equipment', isCorrect: false, displayOrder: 3 },
          ],
        },
      ],
    },
    totalQuestions: 1,
    requiredForJobRoles: ['All Staff'],
    content: [
      {
        id: 4,
        moduleId: 2,
        title: 'Emergency Procedures',
        contentType: 'text' as const,
        contentText: '<p>Emergency preparedness is essential for workplace safety. Every employee must know the emergency procedures.</p><p><strong>Emergency Exits:</strong></p><ul><li>Know the location of all emergency exits</li><li>Keep exits clear and unobstructed</li><li>Never block fire doors</li><li>Know your assembly point outside the building</li></ul><p><strong>Emergency Contacts:</strong></p><ul><li>Emergency services: 999/911</li><li>First aiders: Check notice board for names</li><li>Manager on duty contact number</li></ul><p><strong>Evacuation Procedure:</strong></p><ol><li>Remain calm</li><li>Leave immediately when alarm sounds</li><li>Do not stop to collect belongings</li><li>Use nearest safe exit</li><li>Report to assembly point</li><li>Do not re-enter until cleared by authorities</li></ol>',
        contentUrl: null,
        displayOrder: 1,
        estimatedMinutes: 10,
      },
      {
        id: 5,
        moduleId: 2,
        title: 'Fire Safety',
        contentType: 'text' as const,
        contentText: '<p>Fire safety is everyone\'s responsibility. Understanding fire prevention and response procedures can save lives.</p><p><strong>Fire Prevention:</strong></p><ul><li>Keep flammable materials properly stored</li><li>Report any electrical faults immediately</li><li>Never overload electrical outlets</li><li>Keep work areas clean and tidy</li><li>Properly dispose of oily rags</li></ul><p><strong>Fire Extinguishers:</strong></p><ul><li><strong>Red:</strong> Water (for wood, paper, textiles)</li><li><strong>Blue:</strong> Dry powder (for most fires)</li><li><strong>Cream:</strong> Foam (for liquid fires)</li><li><strong>Black:</strong> CO2 (for electrical fires)</li><li><strong>NEVER</strong> use water on electrical or oil fires</li></ul><p><strong>Fire Response (PASS):</strong></p><ul><li><strong>P</strong> - Pull the pin</li><li><strong>A</strong> - Aim at base of fire</li><li><strong>S</strong> - Squeeze the handle</li><li><strong>S</strong> - Sweep side to side</li></ul><p><em>Remember: Your safety is most important. Only fight small fires. If in doubt, evacuate!</em></p>',
        contentUrl: null,
        displayOrder: 2,
        estimatedMinutes: 15,
      },
    ],
    quizzes: [
      {
        id: 2,
        moduleId: 2,
        title: 'Safety Quiz',
        description: 'Test your safety knowledge',
        passingScore: 70,
        questions: [
          {
            id: 3,
            quizId: 2,
            questionText: 'What does PPE stand for?',
            questionType: QuestionType.SingleChoice,
            points: 10,
            displayOrder: 1,
            answers: [
              { id: 13, questionId: 3, answerText: 'Personal Protective Equipment', isCorrect: true, displayOrder: 1 },
              { id: 14, questionId: 3, answerText: 'Public Protection Equipment', isCorrect: false, displayOrder: 2 },
              { id: 15, questionId: 3, answerText: 'Professional Protective Equipment', isCorrect: false, displayOrder: 3 },
            ],
          },
          {
            id: 4,
            quizId: 2,
            questionText: 'What should you do first when you hear the fire alarm?',
            questionType: QuestionType.SingleChoice,
            points: 10,
            displayOrder: 2,
            answers: [
              { id: 16, questionId: 4, answerText: 'Collect your belongings', isCorrect: false, displayOrder: 1 },
              { id: 17, questionId: 4, answerText: 'Leave immediately', isCorrect: true, displayOrder: 2 },
              { id: 18, questionId: 4, answerText: 'Call your supervisor', isCorrect: false, displayOrder: 3 },
              { id: 19, questionId: 4, answerText: 'Investigate the cause', isCorrect: false, displayOrder: 4 },
            ],
          },
        ],
      },
    ],
    durationMinutes: 30,
    passingScore: 70,
    validityDays: 730,
    isActive: true,
  },
  {
    id: 3,
    categoryId: 1,
    categoryName: 'Food Safety',
    name: 'Advanced HACCP Principles',
    description: 'Hazard Analysis Critical Control Points for managers',
    version: 1,
    status: TrainingStatus.Draft,
    estimatedDurationMinutes: 90,
    validityPeriodDays: 365,
    createdBy: 1,
    createdByName: 'John Admin',
    createdDate: '2024-09-01T10:00:00Z',
    publishedDate: null,
    contents: [],
    quiz: null,
    totalQuestions: 0,
    requiredForJobRoles: ['Store Manager', 'Kitchen Manager'],
    content: [],
    quizzes: [],
    durationMinutes: null,
    passingScore: null,
    validityDays: null,
    isActive: false,
  },
];

// ======================
// TRAINING ASSIGNMENTS
// ======================
export const mockTrainingAssignments: TrainingAssignment[] = [
  {
    id: 1,
    moduleId: 1,
    moduleName: 'Food Hygiene Basics',
    userId: 4,
    userName: 'Alice Employee',
    userEmail: 'alice@example.com',
    assignedDate: '2025-09-01T08:00:00Z',
    dueDate: '2025-10-30T23:59:59Z',
    completedDate: null,
    status: TrainingStatus.Assigned,
    score: null,
    attemptCount: 0,
  },
  {
    id: 2,
    moduleId: 1,
    moduleName: 'Food Hygiene Basics',
    userId: 5,
    userName: 'Charlie Worker',
    userEmail: 'charlie@example.com',
    assignedDate: '2025-09-01T08:00:00Z',
    dueDate: '2025-09-30T23:59:59Z',
    completedDate: '2025-09-25T10:20:00Z',
    status: TrainingStatus.Completed,
    score: 88,
    attemptCount: 1,
  },
  {
    id: 3,
    moduleId: 2,
    moduleName: 'Workplace Safety Fundamentals',
    userId: 4,
    userName: 'Alice Employee',
    userEmail: 'alice@example.com',
    assignedDate: '2025-09-10T08:00:00Z',
    dueDate: '2025-10-10T23:59:59Z',
    completedDate: '2025-10-05T11:00:00Z',
    status: TrainingStatus.Completed,
    score: 95,
    attemptCount: 1,
  },
  {
    id: 4,
    moduleId: 2,
    moduleName: 'Workplace Safety Fundamentals',
    userId: 5,
    userName: 'Charlie Worker',
    userEmail: 'charlie@example.com',
    assignedDate: '2025-09-10T08:00:00Z',
    dueDate: '2025-10-10T23:59:59Z',
    completedDate: '2025-09-20T16:00:00Z',
    status: TrainingStatus.Completed,
    score: 87,
    attemptCount: 2,
  },
  {
    id: 5,
    moduleId: 3,
    moduleName: 'Allergen Awareness',
    userId: 4,
    userName: 'Alice Employee',
    userEmail: 'alice@example.com',
    assignedDate: '2025-08-15T08:00:00Z',
    dueDate: '2025-09-15T23:59:59Z',
    completedDate: '2025-09-10T14:15:00Z',
    status: TrainingStatus.Completed,
    score: 93,
    attemptCount: 1,
  },
  {
    id: 6,
    moduleId: 3,
    moduleName: 'Allergen Awareness',
    userId: 5,
    userName: 'Charlie Worker',
    userEmail: 'charlie@example.com',
    assignedDate: '2025-08-15T08:00:00Z',
    dueDate: '2025-09-15T23:59:59Z',
    completedDate: '2025-09-12T09:30:00Z',
    status: TrainingStatus.Completed,
    score: 90,
    attemptCount: 1,
  },
];

// ======================
// TRAINING COMPLETIONS
// ======================
export const mockTrainingCompletions: TrainingCompletion[] = [
  {
    id: 2,
    assignmentId: 4,
    moduleId: 2,
    moduleName: 'Workplace Safety Fundamentals',
    userId: 5,
    completedDate: '2025-09-20T16:00:00Z',
    score: 87,
    passed: true,
    certificateId: 2,
    validUntil: '2026-09-20T23:59:59Z',
  },
  {
    id: 3,
    assignmentId: 2,
    moduleId: 1,
    moduleName: 'Food Hygiene Basics',
    userId: 5,
    completedDate: '2025-09-25T10:20:00Z',
    score: 88,
    passed: true,
    certificateId: 3,
    validUntil: '2026-09-25T23:59:59Z',
  },
  {
    id: 4,
    assignmentId: 3,
    moduleId: 2,
    moduleName: 'Workplace Safety Fundamentals',
    userId: 4,
    completedDate: '2025-10-05T11:00:00Z',
    score: 95,
    passed: true,
    certificateId: 4,
    validUntil: '2026-10-05T23:59:59Z',
  },
  {
    id: 5,
    assignmentId: 5,
    moduleId: 3,
    moduleName: 'Allergen Awareness',
    userId: 4,
    completedDate: '2025-09-10T14:15:00Z',
    score: 93,
    passed: true,
    certificateId: 5,
    validUntil: '2026-09-10T23:59:59Z',
  },
  {
    id: 6,
    assignmentId: 6,
    moduleId: 3,
    moduleName: 'Allergen Awareness',
    userId: 5,
    completedDate: '2025-09-12T09:30:00Z',
    score: 90,
    passed: true,
    certificateId: 6,
    validUntil: '2026-09-12T23:59:59Z',
  },
];

// ======================
// TRAINING CERTIFICATES
// ======================
export const mockTrainingCertificates: TrainingCertificate[] = [
  {
    id: 2,
    completionId: 2,
    moduleId: 2,
    moduleName: 'Workplace Safety Fundamentals',
    userId: 5,
    userName: 'Charlie Worker',
    issueDate: '2024-09-20T16:00:00Z',
    validUntil: '2026-09-20T23:59:59Z', // Valid for 730 days (2 years)
    certificateNumber: 'OFC-WSF-2024-002',
    isRevoked: false,
    revokedDate: null,
    revokedReason: null,
  },
];

// ======================
// ACTION ITEMS
// ======================
export const mockActionItems: ActionItem[] = [
  {
    id: 1,
    auditId: 1,
    auditName: 'Food Safety Audit - Downtown Store (Oct 5, 2025)',
    title: 'Update temperature log documentation',
    description: 'Temperature logs need to be updated with new format as per recent food safety guidelines. All staff need training on the new format.',
    priority: 'High',
    status: 'Open',
    assignedToUserId: 6,
    assignedToUserName: 'Store Manager - Downtown',
    createdByUserId: 2,
    createdByUserName: 'Jane Auditor',
    createdDate: '2025-10-05T11:30:00Z',
    dueDate: '2025-10-20T23:59:59Z',
    resolvedDate: null,
    resolvedBy: null,
    resolutionNotes: null,
    storeNumber: 'STR001',
    storeName: 'Downtown Store',
    auditorNotes: null,
    closedDate: null,
    closedBy: null,
    closeNotes: null,
  },
  {
    id: 2,
    auditId: 9,
    auditName: 'Food Safety Audit - Westside Store (Oct 1, 2025)',
    title: 'Implement proper food labeling system',
    description: 'Found 3 containers of prepared salad without date labels during audit. Implement comprehensive labeling system for all prepared foods with prep and use-by dates.',
    priority: 'Critical',
    status: 'InProgress',
    assignedToUserId: 9,
    assignedToUserName: 'Store Manager - Westside',
    createdByUserId: 3,
    createdByUserName: 'Bob Inspector',
    createdDate: '2025-10-01T10:50:00Z',
    dueDate: '2025-10-15T23:59:59Z',
    resolvedDate: null,
    resolvedBy: null,
    resolutionNotes: null,
    storeNumber: 'STR002',
    storeName: 'Westside Store',
    auditorNotes: null,
    closedDate: null,
    closedBy: null,
    closeNotes: null,
  },
  {
    id: 3,
    auditId: 2,
    auditName: 'Food Safety Audit - Westside Store (Oct 7, 2025)',
    title: 'Retrain staff on proper PPE usage',
    description: 'One staff member observed not wearing hairnet in back prep area during audit. Schedule mandatory PPE refresher training for all kitchen staff.',
    priority: 'High',
    status: 'InProgress',
    assignedToUserId: 9,
    assignedToUserName: 'Store Manager - Westside',
    createdByUserId: 3,
    createdByUserName: 'Bob Inspector',
    createdDate: '2025-10-07T12:05:00Z',
    dueDate: '2025-10-25T23:59:59Z',
    resolvedDate: null,
    resolvedBy: null,
    resolutionNotes: null,
    storeNumber: 'STR002',
    storeName: 'Westside Store',
    auditorNotes: null,
    closedDate: null,
    closedBy: null,
    closeNotes: null,
  },
  {
    id: 4,
    auditId: 3,
    auditName: 'Health & Safety Audit - Downtown Store (Oct 8, 2025)',
    title: 'Replace expired fire extinguisher',
    description: 'Fire extinguisher near kitchen exit has expired inspection tag (last serviced June 2024). Schedule immediate inspection and servicing.',
    priority: 'Critical',
    status: 'Open',
    assignedToUserId: 7,
    assignedToUserName: 'Maintenance Team',
    createdByUserId: 2,
    createdByUserName: 'Jane Auditor',
    createdDate: '2025-10-08T15:25:00Z',
    dueDate: '2025-10-14T23:59:59Z',
    resolvedDate: null,
    resolvedBy: null,
    resolutionNotes: null,
    storeNumber: 'STR001',
    storeName: 'Downtown Store',
    auditorNotes: null,
    closedDate: null,
    closedBy: null,
    closeNotes: null,
  },
  {
    id: 5,
    auditId: 6,
    auditName: 'Health & Safety Audit - Westside Store (Oct 4, 2025)',
    title: 'Fix exposed electrical wiring',
    description: 'Minor exposed wiring found behind prep station during safety inspection. Requires immediate electrical contractor visit to prevent shock hazard.',
    priority: 'High',
    status: 'Open',
    assignedToUserId: 7,
    assignedToUserName: 'Maintenance Team',
    createdByUserId: 3,
    createdByUserName: 'Bob Inspector',
    createdDate: '2025-10-04T12:35:00Z',
    dueDate: '2025-10-18T23:59:59Z',
    resolvedDate: null,
    resolvedBy: null,
    resolutionNotes: null,
    storeNumber: 'STR002',
    storeName: 'Westside Store',
    auditorNotes: null,
    closedDate: null,
    closedBy: null,
    closeNotes: null,
  },
  {
    id: 6,
    auditId: 4,
    auditName: 'Food Safety Audit - Eastgate Store (Oct 9, 2025)',
    title: 'Install additional hand washing station',
    description: 'Current hand washing setup is insufficient for kitchen size per audit findings. Need to install second station near prep area.',
    priority: 'Medium',
    status: 'Open',
    assignedToUserId: 7,
    assignedToUserName: 'Maintenance Team',
    createdByUserId: 3,
    createdByUserName: 'Bob Inspector',
    createdDate: '2025-10-09T10:50:00Z',
    dueDate: '2025-11-01T23:59:59Z',
    resolvedDate: null,
    resolvedBy: null,
    resolutionNotes: null,
    storeNumber: 'STR003',
    storeName: 'Eastgate Store',
    auditorNotes: null,
    closedDate: null,
    closedBy: null,
    closeNotes: null,
  },
  {
    id: 7,
    auditId: 8,
    auditName: 'Health & Safety Audit - Eastgate Store (Oct 3, 2025)',
    title: 'Update emergency evacuation maps',
    description: 'Emergency exit maps posted in break room are outdated after recent renovations. New maps need to be printed and posted in all required locations.',
    priority: 'Medium',
    status: 'Open',
    assignedToUserId: 8,
    assignedToUserName: 'Store Manager - Eastgate',
    createdByUserId: 2,
    createdByUserName: 'Jane Auditor',
    createdDate: '2025-10-03T14:30:00Z',
    dueDate: '2025-10-25T23:59:59Z',
    resolvedDate: null,
    resolvedBy: null,
    resolutionNotes: null,
    storeNumber: 'STR003',
    storeName: 'Eastgate Store',
    auditorNotes: null,
    closedDate: null,
    closedBy: null,
    closeNotes: null,
  },
  {
    id: 8,
    auditId: 6,
    auditName: 'Health & Safety Audit - Westside Store (Oct 4, 2025)',
    title: 'Repair non-slip flooring in prep area',
    description: 'Non-slip coating is wearing off in high-traffic prep area creating slip hazard. Requires professional recoating service.',
    priority: 'High',
    status: 'Resolved',
    assignedToUserId: 7,
    assignedToUserName: 'Maintenance Team',
    createdByUserId: 3,
    createdByUserName: 'Bob Inspector',
    createdDate: '2025-09-28T09:20:00Z',
    dueDate: '2025-10-10T23:59:59Z',
    resolvedDate: '2025-10-09T14:30:00Z',
    resolvedBy: 'Maintenance Team',
    resolutionNotes: 'Contractor completed non-slip coating replacement on Oct 9. All areas tested and approved for use.',
    storeNumber: 'STR002',
    storeName: 'Westside Store',
    auditorNotes: null,
    closedDate: null,
    closedBy: null,
    closeNotes: null,
  },
  {
    id: 9,
    auditId: 7,
    auditName: 'Food Safety Audit - Downtown Store (Oct 2, 2025)',
    title: 'Organize refrigeration equipment maintenance',
    description: 'Walk-in cooler temperature fluctuations noted during audit. Schedule preventive maintenance inspection for all refrigeration units.',
    priority: 'Medium',
    status: 'Resolved',
    assignedToUserId: 6,
    assignedToUserName: 'Store Manager - Downtown',
    createdByUserId: 2,
    createdByUserName: 'Jane Auditor',
    createdDate: '2025-09-28T08:00:00Z',
    dueDate: '2025-10-05T23:59:59Z',
    resolvedDate: '2025-10-04T11:00:00Z',
    resolvedBy: 'Store Manager - Downtown',
    resolutionNotes: 'HVAC technician inspected all units. Refrigerant topped up and door seals replaced on walk-in cooler.',
    storeNumber: 'STR001',
    storeName: 'Downtown Store',
    auditorNotes: null,
    closedDate: null,
    closedBy: null,
    closeNotes: null,
  },
  {
    id: 10,
    auditId: 1,
    auditName: 'Food Safety Audit - Downtown Store (Oct 5, 2025)',
    title: 'Calibrate digital thermometers',
    description: 'All probe thermometers need calibration testing and documentation as identified during routine food safety audit.',
    priority: 'Medium',
    status: 'Resolved',
    assignedToUserId: 6,
    assignedToUserName: 'Store Manager - Downtown',
    createdByUserId: 2,
    createdByUserName: 'Jane Auditor',
    createdDate: '2025-10-05T11:35:00Z',
    dueDate: '2025-10-12T23:59:59Z',
    resolvedDate: '2025-10-11T15:00:00Z',
    resolvedBy: 'Store Manager - Downtown',
    resolutionNotes: 'All 4 thermometers calibrated against ice bath and boiling water. Results documented and tags updated.',
    storeNumber: 'STR001',
    storeName: 'Downtown Store',
    auditorNotes: null,
    closedDate: null,
    closedBy: null,
    closeNotes: null,
  },
  {
    id: 11,
    auditId: 5,
    auditName: 'Food Safety Audit - Southside Store (Oct 6, 2025)',
    title: 'Deep clean floor drains and grease traps',
    description: 'Floor wet but clean condition noted during audit. Preventive deep cleaning of drains needed to maintain proper drainage.',
    priority: 'Low',
    status: 'Resolved',
    assignedToUserId: 10,
    assignedToUserName: 'Store Manager - Southside',
    createdByUserId: 2,
    createdByUserName: 'Jane Auditor',
    createdDate: '2025-09-28T13:00:00Z',
    dueDate: '2025-10-15T23:59:59Z',
    resolvedDate: '2025-10-10T10:30:00Z',
    resolvedBy: 'Store Manager - Southside',
    resolutionNotes: 'Professional cleaning service completed drain and grease trap cleaning. Drainage improved significantly.',
    storeNumber: 'STR004',
    storeName: 'Southside Store',
    auditorNotes: null,
    closedDate: null,
    closedBy: null,
    closeNotes: null,
  },
  {
    id: 12,
    auditId: 4,
    auditName: 'Food Safety Audit - Eastgate Store (Oct 9, 2025)',
    title: 'Replace damaged cutting boards',
    description: 'Two cutting boards show excessive scoring and wear during audit inspection. Replace to prevent cross-contamination risk.',
    priority: 'Medium',
    status: 'Resolved',
    assignedToUserId: 8,
    assignedToUserName: 'Store Manager - Eastgate',
    createdByUserId: 3,
    createdByUserName: 'Bob Inspector',
    createdDate: '2025-10-01T10:15:00Z',
    dueDate: '2025-10-03T23:59:59Z',
    resolvedDate: '2025-10-02T14:00:00Z',
    resolvedBy: 'Store Manager - Eastgate',
    resolutionNotes: 'Ordered and received color-coded cutting board set. Old boards properly disposed of.',
    storeNumber: 'STR003',
    storeName: 'Eastgate Store',
    auditorNotes: null,
    closedDate: null,
    closedBy: null,
    closeNotes: null,
  },
];

// ======================
// JOB ROLES
// ======================
export const mockJobRoles: JobRole[] = [
  {
    id: 1,
    name: 'Kitchen Staff',
    description: 'Food preparation and kitchen duties',
    isActive: true,
    requiredTrainingModuleIds: [1],
    requiredTrainingModuleNames: ['Food Hygiene Basics'],
  },
  {
    id: 2,
    name: 'Store Manager',
    description: 'Overall store management and supervision',
    isActive: true,
    requiredTrainingModuleIds: [1, 2],
    requiredTrainingModuleNames: ['Food Hygiene Basics', 'Workplace Safety Fundamentals'],
  },
  {
    id: 3,
    name: 'All Staff',
    description: 'General staff members',
    isActive: true,
    requiredTrainingModuleIds: [2],
    requiredTrainingModuleNames: ['Workplace Safety Fundamentals'],
  },
  {
    id: 4,
    name: 'Kitchen Manager',
    description: 'Kitchen operations manager',
    isActive: true,
    requiredTrainingModuleIds: [1],
    requiredTrainingModuleNames: ['Food Hygiene Basics'],
  },
];

// ======================
// NOTIFICATIONS
// ======================
export const mockNotifications: ComplianceNotification[] = [
  {
    id: 1,
    userId: 4,
    title: 'New Training Assigned',
    message: 'You have been assigned "Workplace Safety Fundamentals" training',
    type: 'Training',
    relatedEntityType: 'TrainingAssignment',
    relatedEntityId: 3,
    isRead: false,
    createdDate: '2024-09-10T08:00:00Z',
    readDate: null,
  },
  {
    id: 2,
    userId: 5,
    title: 'Training Due Soon',
    message: 'Your "Food Hygiene Basics" training is due in 7 days',
    type: 'Training',
    relatedEntityType: 'TrainingAssignment',
    relatedEntityId: 2,
    isRead: false,
    createdDate: '2024-09-23T09:00:00Z',
    readDate: null,
  },
  {
    id: 3,
    userId: 2,
    title: 'Audit Pending Approval',
    message: 'Health & Safety Audit for Downtown Store is pending your approval',
    type: 'Audit',
    relatedEntityType: 'Audit',
    relatedEntityId: 3,
    isRead: true,
    createdDate: '2024-10-10T15:20:00Z',
    readDate: '2024-10-10T16:00:00Z',
  },
  {
    id: 4,
    userId: 6,
    title: 'Action Item Assigned',
    message: 'You have been assigned a new action item: Update temperature log documentation',
    type: 'ActionItem',
    relatedEntityType: 'ActionItem',
    relatedEntityId: 1,
    isRead: false,
    createdDate: '2024-10-05T11:30:00Z',
    readDate: null,
  },
];

// ======================
// DASHBOARD METRICS
// ======================
export const mockDashboardMetrics: DashboardMetrics = {
  overallComplianceScore: 92.6,
  riskLevel: 'Low',
  criticalIssuesCount: 0,
  auditCompletionRate: 91.7,
  completedAuditsThisMonth: 11,
  totalAuditsThisMonth: 12,
  averageAuditScore: 92.8,
  auditScoreTrend: 4.2,
  openActionItems: 1,
  criticalActionItems: 0,
  actionItemsCompletedThisWeek: 5,
  actionItemsTrend: -50.0,
};

// ======================
// AUDIT SCHEDULES
// ======================
export const mockAuditSchedules: AuditSchedule[] = [
  {
    id: 1,
    templateId: 1,
    templateName: 'Food Safety Checklist v3.0',
    assignmentType: 'stores',
    storeNumbers: ['STR001'],
    regionIds: [],
    storeName: 'Downtown Store',
    cronExpression: '0 0 9 ? * MON,THU *', // Example cron for Monday/Thursday 9am
    isActive: true,
    assignToRoleId: 1, // Store Manager
    assignToUserIds: [2, 3], // Multiple auditors assigned
    assignToUserId: undefined,
    notifyOnCreate: true,
    nextScheduledDate: '2024-10-07T00:00:00Z', // Next Monday
    createdDate: '2024-08-25T10:00:00Z',
    updatedDate: '2024-09-15T14:30:00Z',
    isAdHoc: false,
  },
  {
    id: 2,
    templateId: 2,
    templateName: 'H&S Compliance Check v2.1',
    assignmentType: 'stores',
    storeNumbers: ['STR001'],
    regionIds: [],
    storeName: 'Downtown Store',
    cronExpression: '0 0 9 1 * ? *', // Example cron for 1st of month 9am
    isActive: true,
    assignToRoleId: 2, // Safety Officer
    assignToUserIds: [3], // Single auditor in array format
    assignToUserId: undefined,
    notifyOnCreate: true,
    nextScheduledDate: '2024-11-01T00:00:00Z', // Next month
    createdDate: '2024-08-20T09:00:00Z',
    updatedDate: undefined,
    isAdHoc: false,
  },
  {
    id: 3,
    templateId: 1,
    templateName: 'Food Safety Checklist v3.0',
    assignmentType: 'stores',
    storeNumbers: ['STR002'],
    regionIds: [],
    storeName: 'Westside Store',
    cronExpression: '0 0 9 ? * TUE,FRI *', // Tuesday/Friday 9am
    isActive: true,
    assignToRoleId: 1, // Store Manager
    assignToUserIds: [2, 4], // Multiple auditors assigned
    assignToUserId: undefined,
    notifyOnCreate: true,
    nextScheduledDate: '2024-10-04T00:00:00Z', // Next Friday
    createdDate: '2024-08-25T10:30:00Z',
    updatedDate: undefined,
    isAdHoc: false,
  },
  {
    id: 4,
    templateId: 2,
    templateName: 'H&S Compliance Check v2.1',
    assignmentType: 'stores',
    storeNumbers: ['STR003'],
    regionIds: [],
    storeName: 'Eastgate Store',
    cronExpression: '0 0 9 15 * ? *', // 15th of month 9am
    isActive: true,
    assignToRoleId: 2, // Safety Officer
    assignToUserId: undefined,
    notifyOnCreate: true,
    nextScheduledDate: '2024-10-15T00:00:00Z',
    createdDate: '2024-09-01T11:00:00Z',
    updatedDate: undefined,
    isAdHoc: false,
  },
  {
    id: 5,
    templateId: 1,
    templateName: 'Food Safety Checklist v3.0',
    assignmentType: 'stores',
    storeNumbers: ['STR003'],
    regionIds: [],
    storeName: 'Eastgate Store',
    cronExpression: '0 0 9 */2 * ? *', // Every 2 days at 9am
    isActive: true,
    assignToRoleId: 1, // Store Manager
    assignToUserId: undefined,
    notifyOnCreate: false, // No notifications for frequent audits
    nextScheduledDate: '2024-10-04T00:00:00Z',
    createdDate: '2024-09-18T08:00:00Z',
    updatedDate: undefined,
    isAdHoc: false,
  },
  {
    id: 6,
    templateId: 1,
    templateName: 'Food Safety Checklist v3.0',
    assignmentType: 'stores',
    storeNumbers: ['STR001'],
    regionIds: [],
    storeName: 'Downtown Store',
    cronExpression: '0 0 9 ? * WED *', // Wednesday 9am
    isActive: false, // Inactive - ended schedule
    assignToRoleId: 1,
    assignToUserId: undefined,
    notifyOnCreate: true,
    nextScheduledDate: undefined, // No next date - schedule ended
    createdDate: '2024-07-28T10:00:00Z',
    updatedDate: '2024-10-01T09:00:00Z',
    isAdHoc: false,
  },
];

export const mockAuditScoreTrends: AuditScoreTrend[] = [
  { month: 'Apr 2024', averageScore: 86.5, auditCount: 10 },
  { month: 'May 2024', averageScore: 88.2, auditCount: 11 },
  { month: 'Jun 2024', averageScore: 87.8, auditCount: 12 },
  { month: 'Jul 2024', averageScore: 89.5, auditCount: 13 },
  { month: 'Aug 2024', averageScore: 90.8, auditCount: 12 },
  { month: 'Sep 2024', averageScore: 92.8, auditCount: 11 },
];

export const mockTrainingCompletionTrends: TrainingCompletionTrend[] = [
  { month: 'Apr 2024', completionRate: 82, assignedCount: 18, completedCount: 15 },
  { month: 'May 2024', completionRate: 85, assignedCount: 20, completedCount: 17 },
  { month: 'Jun 2024', completionRate: 87, assignedCount: 19, completedCount: 17 },
  { month: 'Jul 2024', completionRate: 88, assignedCount: 17, completedCount: 15 },
  { month: 'Aug 2024', completionRate: 90, assignedCount: 16, completedCount: 14 },
  { month: 'Sep 2024', completionRate: 93, assignedCount: 15, completedCount: 14 },
];

